import React, { useState, useEffect, useRef, useContext, useCallback } from 'react';
import { Box } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import { useTranslation } from 'react-i18next';

import { getMaterialUrl } from '../../config/externalMaterials';
import { AuthContext } from '../../authContext';
import AlertsCustom from '../../components/Alerts/index';
import Filtros from '../../components/Filtros';
import Message from '../../components/Message';
import { frozenStepDurationMs, frozenToolDurationMs } from '../../components/ActivityStepInline/elapsedTime';
import Modelos from '../../components/Modelos';
import OneDriveFileButton from '../../components/OneDriveFileButton';
import UploadButton from '../../components/UploadB';
import './UnifiedChat.css';
import { getDefaultChatModel } from '../../utils/assistantModels';
import { translateBackendError } from '../../utils/translateBackendError';
import { getFriendlyError, buildUploadAlert, aplicarProgressoDeExecucao, idDaMensagem, idsQueFecharam } from './helpers';
import { enrichHistoryWithActivity } from '../../components/ActivityStepInline/buildActivityFromExecutionEvents';
import { useOpenRouter } from '../../context/OpenRouterContext';

let clientIdCounter = 0;
// Id estável gerado no client para usar como React key, independente de quando/se o backend atribui um _id real.
const generateClientId = () => `client-${++clientIdCounter}`;

// Nome da skill-creator real (mesmo valor de SKILL_CREATOR_COMMAND em
// backend/skills_helpers/skills_detection.py) - skill de plataforma, tratada
// como exclusiva do botao fixo "+ Criar Skill": nao deve aparecer como linha
// na lista do dropdown "/" (nem duplicada, nem sozinha), so' pelo botao.
const SKILL_CREATOR_NAME = 'skill-creator';

// Filtro do dropdown "/" pelo texto digitado apos a barra - mesmo padrao
// case-insensitive por substring de matchesSearch/matchesShareQuery
// (SkillsManagerModal/ShareSkillModal).
function matchesSlashQuery(skill, query) {
    const normalized = query.trim().toLowerCase();
    if (!normalized) return true;
    return skill.name.toLowerCase().includes(normalized);
}

// Contrato de POST /post_execution_progress: ~5 s, e so' enquanto houver
// mensagem nao terminal na sessao aberta.
const INTERVALO_POLL_EXECUCAO_MS = 5000;

function UnifiedChat({
    chatHistory = [],
    setChatHistory = () => { },
    classeCSS,
    loading,
    sessionId,
    totalCost = 0,
    setTotalCost = () => { },
    user,
    setLinks = () => { },
    assistant,
    assistantId,
    isMobile = false,
    createNewChat,
    openHistoricoOverlay,
    summaryInfo = {},
    updateSummary = () => { },
    setSessions = () => { },
    selectedModel,
    setSelectedModel = () => { },
    messageSent,
    setMessageSent = () => { },
    setShowNovidadesPopup = () => { },
    useTestBox = false,
    model: modelProp,
    setModel: setModelProp = () => { },
    email,
    setSummaryInfo = () => { },
    llmProvider,
    createSkillSignal = 0,
    selectSkillRequest = null,
    editSkillRequest = null,
    // front_planning_tools: opcionais - quando o pai (ex: Chat/index.js) quer renderizar
    // o painel "Atividade" como coluna própria do layout (mesma altura/espaçamento do
    // histórico), ele controla esse estado e passa aqui. Sem isso, cai num fallback
    // interno (states abaixo) que simplesmente não é observado por ninguém de fora.
    activitySteps: activityStepsProp,
    setActivitySteps: setActivityStepsProp,
    activityPanelOpen: activityPanelOpenProp,
    setActivityPanelOpen: setActivityPanelOpenProp,
    activityPanelMessageId: activityPanelMessageIdProp,
    setActivityPanelMessageId: setActivityPanelMessageIdProp,
    // Abaixo de 1024px (ver Chat/index.js): painel "Atividade" nunca abre - indicador
    // inline e pill persistente continuam aparecendo, só sem seta/clique (mesmo mecanismo
    // de hideActivitySummaryCaret já usado na prévia, Tarefa 8).
    hideActivityPanel = false
}) {
    const { accessToken, newSessionId, fetchResourceGroup, resourceGroup, setSessionId: setSessionIdFromContext, level } = useContext(AuthContext);
    const { useOpenrouter } = useOpenRouter();
    const isPreview = Boolean(useTestBox);
    const resolvedUser = user || email;
    const resolvedModel = selectedModel || getDefaultChatModel(assistantId);
    const menuRef = useRef(null);
    const [showAttachMenu, setShowAttachMenu] = useState(false);
    const slashMenuRef = useRef(null);
    const [showSlashMenu, setShowSlashMenu] = useState(false);
    const [availableSkills, setAvailableSkills] = useState([]);
    const [skillsLoading, setSkillsLoading] = useState(false);
    // FR-078 (Tarefa 11.5): erro/timeout de GET /get_skills ao abrir o
    // dropdown "/" - distinto de lista vazia (mesmo padrao do modal
    // "Gerenciador de Skills", SkillsManagerModal/index.js).
    const [skillsError, setSkillsError] = useState(false);
    // Identificador da busca em andamento - evita que uma resposta atrasada
    // (de uma abertura anterior do dropdown, ja fechada e reaberta) sobrescreva
    // o resultado de uma busca mais recente (substitui o antigo `cancelled`
    // local do efeito, que nao dava pra reutilizar no botao "tentar de novo").
    const skillsFetchIdRef = useRef(0);
    // Tarefa 2.2: item destacado do dropdown "/" via teclado/mouse - 0 e'
    // sempre "Criar Skill", 1..N mapeia para availableSkills[index - 1].
    const [highlightedIndex, setHighlightedIndex] = useState(0);
    // Tarefa 5.2 (FR-024): posicao da "/" (dentro de `input`) que abriu o
    // dropdown atualmente - nao precisa mais ser sempre 0 (inicio da
    // mensagem). -1 quando nenhum comando esta sendo composto. Usado para
    // preservar o texto digitado ANTES da "/" ao inserir o comando escolhido.
    const [slashCommandIndex, setSlashCommandIndex] = useState(-1);
    // Tarefa 6.4 (FR-039): sinalizador oculto (nao visivel no texto da
    // mensagem) que marca a PROXIMA mensagem enviada como originada da acao
    // "Editar skill no chat" - lido e resetado dentro de sendMessage, para
    // valer so para aquele envio especifico.
    const [pendingSkillEditIntent, setPendingSkillEditIntent] = useState(false);
    // Marca o texto exato do comando de skill ("/nome-da-skill" ou
    // "/skill-creator") inserido pelo dropdown "/" - usado so' para o
    // destaque azul continuar aparecendo na bolha da mensagem apos o envio
    // (Message/index.js), lido e resetado dentro de sendMessage.
    const [pendingSkillCommandText, setPendingSkillCommandText] = useState(null);
    const messageInputRef = useRef(null);
    const [interactionId, setInteractionId] = useState(null);
    const [isTyping, setIsTyping] = useState(false);
    const [alertTitle, setAlertTitle] = useState("");
    const [alertType, setAlertType] = useState("");
    const [alertMessage, setAlertMessage] = useState("");
    const [showAlert, setShowAlert] = useState(false);
    const [cooldown, setCooldown] = useState(false);
    const [uploadingCount, setUploadingCount] = useState(0);
    const isUploading = uploadingCount > 0;
    const [uploadedFiles, setUploadedFiles] = useState([]);
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [failedFiles, setFailedFiles] = useState([]);
    const [responseLinks, setResponseLinks] = useState([]);
    let geminiKey;
    let blobConnection;
    let blobContainer;

    // REF para rastrear a sess�o atual e evitar vazamento de mensagens de chats anteriores
    const currentSessionRef = useRef(sessionId);

    const { i18n } = useTranslation();

    const infoUrl = getMaterialUrl('guiaPromptsChatbox', i18n.language);

    const { t: tChat } = useTranslation('chat');
    const { t: tAssistantConfiguration } = useTranslation('assistantConfiguration');
    const { t: tCommon } = useTranslation('common');
    const { t: tAlerts } = useTranslation('alerts');
    // Each suggestion has a label (shown on the button) and a prompt (sent to the assistant).
    // This lets the UI text be different from the actual prompt that should be dispatched.
    const suggestionButtons = [
        { labelKey: 'suggestion_first', promptKey: 'suggestion_first_prompt', className: 'first_card_suggestion' },
        { labelKey: 'suggestion_second', promptKey: 'suggestion_second_prompt', className: 'second_card_suggestion' },
        { labelKey: 'suggestion_third', promptKey: 'suggestion_third_prompt', className: 'third_card_suggestion' },
        { labelKey: 'suggestion_fourth', promptKey: 'suggestion_fourth_prompt', className: 'fourth_card_suggestion' }
    ];

    const initialGreeting = () => {
        if (assistantId === '1') {
            return tChat('core_greetings');
        } else if (assistantId === '3') {
            return tChat('site_assist_greetings');
        } else if (assistantId === '2') {
            return tChat('sesan_greetings');
        }
        return tChat('default_greetings', { assistant });
    };

    const [messages, setMessages] = useState(() =>
        isPreview
            ? [{ role: 'system', content: '' }]
            : [
                { role: 'system', content: '' },
                { role: 'assistant', content: initialGreeting(), feedback: "", _id: "", clientId: generateClientId() }
            ]
    );
    const [input, setInput] = useState('');
    const messagesEndRef = useRef(null);
    const [filtro, setFiltro] = useState({});
    const [cost, setCost] = useState('0.00'); // Estado para armazenar o custo da sess�o
    const [isSending, setIsSending] = useState(false);
    const [showTooltip, setShowTooltip] = useState(false);
    const [internalActivitySteps, setInternalActivitySteps] = useState([]);
    const [internalActivityPanelOpen, setInternalActivityPanelOpen] = useState(false);
    const [internalActivityPanelMessageId, setInternalActivityPanelMessageId] = useState(null);
    const activitySteps = activityStepsProp ?? internalActivitySteps;
    const setActivitySteps = setActivityStepsProp ?? setInternalActivitySteps;
    const activityPanelOpen = activityPanelOpenProp ?? internalActivityPanelOpen;
    const setActivityPanelOpen = setActivityPanelOpenProp ?? setInternalActivityPanelOpen;
    const activityPanelMessageId = activityPanelMessageIdProp ?? internalActivityPanelMessageId;
    const setActivityPanelMessageId = setActivityPanelMessageIdProp ?? setInternalActivityPanelMessageId;
    const model = resolvedModel;
    const summarySessionId = summaryInfo?.session_id || sessionId;
    const isChatEmpty = messages.filter(msg => msg.role === 'user').length === 0;

    const fileInputRef = useRef(null);
    const [isInputFocused, setIsInputFocused] = useState(false);
    const [n8nTools, setN8nTools] = useState([]);
    const [assistantLlmProvider, setAssistantLlmProvider] = useState(llmProvider || null);
    const [localClusterModel, setLocalClusterModel] = useState(null);
    const [localClusterModelVision, setLocalClusterModelVision] = useState(false);
    const [publicClusterModels, setPublicClusterModels] = useState([]);
    const [keyboardOpen, setKeyboardOpen] = useState(false);
    const [autoScroll, setAutoScroll] = useState(true);
    const containerRef = useRef(null);

    // Detecta teclado virtual em dispositivos m�veis usando visualViewport ou resize
    useEffect(() => {
        if (!isMobile) {
            setKeyboardOpen(false);
            return;
        }

        const threshold = 150; // px: diferen�a m�nima para considerar teclado aberto
        const initialHeight = window.visualViewport ? window.visualViewport.height : window.innerHeight;

        const checkViewport = () => {
            const height = window.visualViewport ? window.visualViewport.height : window.innerHeight;
            setKeyboardOpen(initialHeight - height > threshold);
        };

        if (window.visualViewport) {
            window.visualViewport.addEventListener('resize', checkViewport);
            window.visualViewport.addEventListener('scroll', checkViewport);
        } else {
            window.addEventListener('resize', checkViewport);
        }

        // fallback: pequenas heur�sticas com focus/blur de window
        const onFocus = () => setKeyboardOpen(true);
        const onBlur = () => setTimeout(() => setKeyboardOpen(false), 50);
        window.addEventListener('focus', onFocus);
        window.addEventListener('blur', onBlur);

        return () => {
            if (window.visualViewport) {
                window.visualViewport.removeEventListener('resize', checkViewport);
                window.visualViewport.removeEventListener('scroll', checkViewport);
            } else {
                window.removeEventListener('resize', checkViewport);
            }
            window.removeEventListener('focus', onFocus);
            window.removeEventListener('blur', onBlur);
        };
    }, [isMobile]);

    useEffect(() => {
        if (Array.isArray(chatHistory) && chatHistory.length > 0) {
            setMessages(chatHistory); // Adiciona o dicion�rio e o hist�rico ao estado interno
        }
    }, [chatHistory]);

    // ------------------------------------------------------------------
    // Progresso de execucao (T047-T050, specs/execution_progress/)
    //
    // O SSE so' existe enquanto ESTA aba esta conectada. Se o usuario
    // recarrega a pagina, troca de conversa e volta, ou abre o chat noutro
    // dispositivo, a execucao continua rodando no servidor mas nao chega
    // evento nenhum aqui. O poll cobre exatamente essa janela: pergunta ao
    // servidor o estado das mensagens que ainda nao fecharam.
    //
    // O gatilho e' o campo `status`, que so' existe em mensagem vinda do
    // historico (_build_assistant_message no backend/chat/views.py). Mensagem
    // sendo montada ao vivo pelo SSE nao tem esse campo, entao o poll nao
    // concorre com o streaming - as duas fontes nunca escrevem juntas.
    // ------------------------------------------------------------------
    //
    // So' `running`. `interrupted` e' terminal do ponto de vista da interface
    // (T059): a rota continua devolvendo essas mensagens - e' assim que a
    // transicao running -> interrupted chega aqui - mas assim que ela chega,
    // o id sai desta lista e o poll para sozinho. Continuar perguntando sobre
    // uma execucao que ja' morreu so' gastaria request.
    const idsEmExecucao = messages
        .filter(msg => msg.role === 'assistant' && msg.status === 'running')
        .map(msg => msg.interaction_id || msg._id)
        .filter(Boolean);
    // String em vez do array nas deps: array novo a cada render reiniciaria o
    // setInterval em todo re-render do chat.
    const idsEmExecucaoKey = idsEmExecucao.join(',');

    // Mesma cadeia label -> i18n por `name` que o fluxo ao vivo usa nas tools
    // da skill: sem ela a trilha reidratada apareceria com o nome cru da tool
    // (write_todos, read_file...) ou com o docstring cru das tools nativas.
    const rotuloDaTool = useCallback(
        evento => tChat(`executionTrail.tools.${evento.name}`, { defaultValue: evento.label || evento.name }),
        [tChat],
    );

    // T049: quando a execucao fecha, o texto final so' existe no documento -
    // e' o caminho de historico que ja' existe que o traz, sem reload.
    const recarregarHistorico = useCallback(async () => {
        try {
            const resp = await fetch(`${process.env.REACT_APP_API_URL}/post_session`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${accessToken}`,
                },
                body: JSON.stringify({ session_id: sessionId, assistant_id: assistantId }),
            });
            if (!resp.ok) return;
            const data = await resp.json();
            if (Array.isArray(data.history)) {
                // Achado 2026-09-15 (card 1256, specs/front_planning_tools/plan.md): faltava
                // reconstruir activity_summary/activity_timeline aqui, igual o carregamento
                // inicial do chat (Historico/index.js::fetchSessionHistory) já faz - sem isso,
                // quando uma execução termina DE VERDADE entre duas batidas do polling
                // (idsQueFecharam -> recarregarHistorico), a mensagem ficava sem pill nem chip
                // da skill até o usuário sair e voltar pro chat de novo.
                setChatHistory(enrichHistoryWithActivity(data.history, tChat));
            }
        } catch (erro) {
            console.error('Erro ao recarregar o historico da sessao:', erro);
        }
    }, [sessionId, assistantId, accessToken, setChatHistory, tChat]);

    useEffect(() => {
        if (!sessionId || idsEmExecucaoKey === '') return undefined;

        const idsObservados = idsEmExecucaoKey.split(',');
        let cancelado = false;

        const consultarProgresso = async () => {
            let data;
            try {
                const resp = await fetch(`${process.env.REACT_APP_API_URL}/post_execution_progress`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${accessToken}`,
                    },
                    body: JSON.stringify({ session_id: sessionId, assistant_id: assistantId }),
                });
                if (!resp.ok) return;
                data = await resp.json();
            } catch (erro) {
                // Falha de rede nao pode derrubar o poll: a proxima batida
                // tenta de novo, e ate' la' a tela mantem o ultimo estado.
                console.error('Erro ao consultar o progresso de execucao:', erro);
                return;
            }
            if (cancelado) return;

            // A fusao mora em helpers.js (com teste proprio): nao duplicar
            // mensagem e nao escrever `content` sao as duas regras que fazem
            // o poll conviver com o streaming sem estragar a tela.
            const aplicarProgresso = prev => aplicarProgressoDeExecucao(prev, data.messages, {
                rotuloDaTool,
            });

            setMessages(aplicarProgresso);
            setChatHistory(aplicarProgresso);

            // Sair de `messages[]` significa que a execucao virou terminal -
            // e' o sinal de parada documentado no contrato da rota.
            if (idsQueFecharam(idsObservados, data.messages).length > 0) {
                recarregarHistorico();
            }
        };

        consultarProgresso();
        const intervalo = setInterval(consultarProgresso, INTERVALO_POLL_EXECUCAO_MS);
        return () => {
            cancelado = true;
            clearInterval(intervalo);
        };
    }, [idsEmExecucaoKey, sessionId, assistantId, accessToken, setChatHistory, recarregarHistorico, rotuloDaTool]);

    useEffect(() => {
        // Atualiza a ref para a nova sess�o
        currentSessionRef.current = sessionId;

        setInteractionId(null); // nova sess�o = novo chat
        setUploadedFiles([]);
        setFailedFiles([]);
        setActivitySteps([]);
        setActivityPanelOpen(false);
        setActivityPanelMessageId(null);

    }, [sessionId]);

    // Para self-assistants (IDs diferentes de 0,1,2,3), busca a configuração do assistente
    // para hidratar provider/local cluster model e as tools n8n ativas.
    useEffect(() => {
        const isSelfAssistant = assistantId && !["0", "1", "2", "3"].includes(assistantId);

        if (!isSelfAssistant) {
            return;
        }

        const fetchAssistantConfig = async () => {
            try {
                const response = await fetch(`${process.env.REACT_APP_API_URL}/post_data_assistant`, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": `Bearer ${accessToken}`
                    },
                    body: JSON.stringify({
                        assistant_id: assistantId,
                    }),
                });

                if (!response.ok) {
                    console.error('Erro ao obter configuração do assistente para web search');
                    return;
                }

                const data = await response.json();
                const provider = data?.data?.llm_provider || null;
                setAssistantLlmProvider(provider);
                const clusterModel = data?.data?.local_cluster_config?.model || null;
                const clusterVision = Boolean(data?.data?.local_cluster_config?.vision_capable);
                if (provider === 'local_cluster' && clusterModel) {
                    setLocalClusterModel(clusterModel);
                    setLocalClusterModelVision(clusterVision);
                }

                // Load active n8n tools for the tool selector in chat
                const n8nEnabled = Boolean(data?.data?.n8n_tools_enabled);
                const tools = Array.isArray(data?.data?.n8n_tools) ? data.data.n8n_tools : [];
                setN8nTools(n8nEnabled ? tools.filter(t => t.active) : []);
            } catch (error) {
                console.error('Erro ao buscar configuração do assistente:', error);
            }
        };

        fetchAssistantConfig();
    }, [assistantId, accessToken]);

    useEffect(() => {
        const timeout = setTimeout(() => {
            window.dispatchEvent(new Event('resize'));
        }, 0);

        return () => clearTimeout(timeout);
    }, []);

    // Show alert when OpenRouter credits are low
    useEffect(() => {
        if (!useOpenrouter) {
            setAlertTitle(tChat('low_credits_title'));
            setAlertMessage(tChat('low_credits_message'));
            setAlertType('warning');
            setShowAlert(true);
        }
    }, [useOpenrouter, tChat]);

    useEffect(() => {
        const fetchPublicClusterModels = async () => {
            try {
                const response = await fetch(`${process.env.REACT_APP_API_URL}/get_public_cluster_models`, {
                    headers: {
                        Authorization: `Bearer ${accessToken}`
                    }
                });

                if (!response.ok) {
                    return;
                }

                const data = await response.json();
                if (data.status && Array.isArray(data.models)) {
                    setPublicClusterModels(data.models.map(model => model.id));
                }
            } catch (error) {
                console.error('Erro ao buscar modelos locais públicos:', error);
            }
        };

        if (accessToken) {
            fetchPublicClusterModels();
        }
    }, [accessToken]);

    // Função para normalizar a estrutura de um arquivo do OneDrive para o formato de arquivos padrão
    // adicionando campos faltantes
    const normalizeOneDriveFiles = (oneDriveFiles) => {
        return oneDriveFiles.map(file => {
            const name = file.name || file.filename || 'unknown';
            const ext = name.split('.').pop().toLowerCase();
            const mimeTypes = { // mapping das extensões para tipos de arquivo
                'pdf': 'application/pdf',
                'png': 'image/png',
                'jpg': 'image/jpeg',
                'jpeg': 'image/jpeg',
                'gif': 'image/gif',
                'webp': 'image/webp',
                'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                'xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            };

            return {
                name: file.name || file.filename,
                filename: file.name || file.filename,
                type: mimeTypes[ext],
                size: file.size,
                isOneDrive: true,
                oneDriveData: file
            }
        })
    }

    // Função para fazer upload dos arquivos
    // agora recebe como parametro 'isOneDrive' para diferenciar entre upload de arquivos normais e de OneDrive
    const handleFileUpload = async (files, isOneDrive = false) => {
        setUploadingCount(c => c + 1);

        const resourcesData = resourceGroup?.reduce((acc, resource) => ({
            geminiKey: resource?.pdf_processing?.key_gemini ?? acc.geminiKey,
            blobConnection: resource?.pdf_processing?.connection_string ?? acc.blobConnection,
            blobContainer: resource?.pdf_processing?.container_name ?? acc.blobContainer
        }), {
            geminiKey: null,
            blobConnection: null,
            blobContainer: null
        });

        const formData = new FormData();
        formData.append('session_id', sessionId);
        formData.append('user', resolvedUser);
        formData.append('model', model);
        formData.append('resources', JSON.stringify(resourcesData));

        if (isOneDrive) {
            formData.append('one_drive_urls', JSON.stringify(files));

        } else {
            // Adiciona cada arquivo ao FormData
            files.forEach((file) => {
                formData.append('files', file);
                formData.append('filenames', file.name || file.filename);
            });
        }

        try {
            const resposta = await fetch(`${process.env.REACT_APP_API_URL}/post_upload_file`, {
                method: "POST",
                headers: {
                    "Authorization": `Bearer ${accessToken}`
                },
                body: formData,
                // headers s�o automaticamente definidos como multipart/form-data
            });

            if (resposta.ok) {
                const result = await resposta.json();

                if (result.model_changed) {
                    // trocar o modelo selecionado
                    handleSetModel(result.new_model);
                }

                if (result.process_files) {
                    const successfulFiles = result.process_files.filter(f => f.status === true);
                    const failed = result.process_files.filter(f => f.status === false);

                    setUploadedFiles(prev => [...prev, ...successfulFiles]);
                    setFailedFiles(prev => [...prev, ...failed]);

                    if (failed.length > 0) {
                        const failedNames = new Set(failed.map(f => f.filename));
                        setSelectedFiles(prev => prev.filter(f => !failedNames.has(f.name || f.filename)));
                    }

                    const alert = buildUploadAlert(
                        result,
                        failed,
                        { tChat, tAlerts }
                    );

                    if (alert.show) {
                        setAlertTitle(alert.title);
                        setAlertType(alert.type);
                        setAlertMessage(alert.message);
                        setShowAlert(true);
                    }

                } else if (result.files) {
                    setUploadedFiles(prev => [...prev, ...result.files]);
                }

            } else {

                const errorData = await resposta.json();

                if (errorData.error_type === "token_limit") {
                    setAlertTitle(tAlerts('file_tokens_title'));
                    setAlertType("error");
                    setAlertMessage(
                        `${errorData.filename}: ${tAlerts('file_tokens_limit')}`
                    );
                    setShowAlert(true);
                    return;
                }

                // Outros erros do upload (ex.: limite agregado de tamanho de 200MB,
                // nenhum arquivo enviado, erro interno) chegam como error_code — mostra
                // traduzido em vez de engolir silenciosamente (antes só ia pro console).
                if (errorData.error) {
                    setAlertTitle(tChat('alertErrorTitle'));
                    setAlertType("error");
                    setAlertMessage(translateBackendError(errorData.error));
                    setShowAlert(true);
                    return;
                }

                console.error('Erro no upload:', resposta.status);
                return;

            }
        } catch (error) {
            console.error('Erro na requisição:', error);
        } finally {
            setUploadingCount(c => c - 1);
            setCooldown(true);
            setTimeout(() => setCooldown(false), 2000);
        }
    };


    // Efeito para verificar se o usu�rio deve ver o pop-up de novidades
    useEffect(() => {
        const checkUserUpdate = async () => {
            // Apenas para o "Chat Iris" (assistantId: 0) e se o usu�rio estiver definido
            if (assistantId === '0' && resolvedUser) {
                try {
                    const response = await fetch(`${process.env.REACT_APP_API_URL}/post_check_update`, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${accessToken}`
                        },
                        body: JSON.stringify({ email: resolvedUser })
                    });

                    if (response.ok) {
                        const data = await response.json();

                        if (data.update) {
                            setShowNovidadesPopup(true);
                        }
                    }
                } catch (error) {
                    console.error('Erro ao verificar atualização para o usuário:', error);
                }
            }
        };

        checkUserUpdate();
    }, [assistantId, resolvedUser, accessToken, setShowNovidadesPopup]);


    const handleSetModel = (nextModel) => {
        const nextIsLocal = nextModel === localClusterModel || publicClusterModels.includes(nextModel);
        if (nextIsLocal) {
            const incompatibleTypes = ['image/', 'application/pdf'];
            setSelectedFiles(prev => prev.filter(f => !incompatibleTypes.some(t => f.type?.startsWith(t))));
            setUploadedFiles(prev => prev.filter(f => !incompatibleTypes.some(t => f.type?.startsWith(t))));
        }
        setSelectedModel(nextModel);
        setModelProp(nextModel);
    };

    const effectiveModel = selectedModel || getDefaultChatModel(assistantId);

    const isLocalModel = effectiveModel === localClusterModel || publicClusterModels.includes(effectiveModel);

    const getMessageText = useCallback((msg) => {
        if (!msg) return '';
        if (typeof msg.content === 'string') return msg.content;
        if (Array.isArray(msg.content)) {
            return msg.content
                .filter(part => part && part.type === 'text' && typeof part.text === 'string')
                .map(part => part.text)
                .join('\n');
        }
        return '';
    }, []);



    const handleFilesSelected = (files) => {
        if (isPreview && !assistantId) return;
        const allFiles = [...selectedFiles, ...files];
        const allImages = allFiles.filter(file => file.type && file.type.startsWith("image/"));
        const totalImageSizeBytes = allImages.reduce((acc, file) => acc + (file.size || 0), 0);
        const totalImageSizeMB = totalImageSizeBytes / (1024 * 1024);

        if (totalImageSizeMB > 35) {
            setAlertType('error');
            setAlertTitle(tChat("limite_img"));
            setAlertMessage(tChat("alerta_message"));
            setShowAlert(true);
            return;
        }

        setSelectedFiles(allFiles);
        handleFileUpload(files);
    };



    const sendMessage = async ({
        text,
        files,
        resetInput = true,
        resetSelectedFiles = true,
        resetFileInput = true
    } = {}) => {
        // Captura o ID da sess�o no momento do envio
        const sendingSessionId = sessionId;

        // Tarefa 6.4 (FR-039): captura o sinalizador de edicao no momento do
        // envio e ja reseta o estado - vale so para esta mensagem, nao para
        // qualquer envio futuro apos "Editar skill no chat".
        const sendingSkillEditIntent = pendingSkillEditIntent;
        if (pendingSkillEditIntent) {
            setPendingSkillEditIntent(false);
        }

        // So' preserva o marcador do comando de skill se o texto final ainda
        // o contem (ex.: usuario nao apagou o span azul antes de enviar) -
        // usado apenas para o destaque azul continuar na bolha da mensagem
        // enviada (Message/index.js), nao afeta deteccao/execucao de skill
        // no backend.
        const sendingSkillCommandText = pendingSkillCommandText;
        if (pendingSkillCommandText) {
            setPendingSkillCommandText(null);
        }

        const messageValue = text !== undefined ? text : input;
        const filesInMessage = files !== undefined ? files : selectedFiles;

        if ((!messageValue || messageValue.trim() === '') && filesInMessage.length === 0) {
            return;
        }

        const skillCommandText = (sendingSkillCommandText && messageValue.includes(sendingSkillCommandText))
            ? sendingSkillCommandText
            : null;

        const userMessage = {
            role: "user",
            clientId: generateClientId(),
            content: [{ type: "text", text: messageValue || '' }],
            ...(skillCommandText ? { skillCommandText } : {}),
            files: filesInMessage.map(f => {
                const isImage = f.type.startsWith("image/");

                // lógica para diferenciar a preview de imagens oriundas do OneDrive ou arquivos locais
                let preview = null;
                if (isImage && f.oneDriveData) {
                    preview = f.oneDriveData.downloadUrl || f.oneDriveData.webUrl || null;
                } else if (isImage && !f.oneDriveData) {
                    preview = URL.createObjectURL(f);
                }

                return {
                    name: f.name || f.filename,
                    type: f.type,
                    // para images oriundas do OneDrive, esse método URL não funciona 
                    preview: preview || null
                };
            })
        };

        const auxiliar_message = [...messages, userMessage];

        // Dispara a geração do título em paralelo com a resposta principal,
        // em vez de esperar o interaction_id no fim do stream - o título só
        // depende da pergunta do usuário (já disponível aqui), não da resposta
        // do assistente, então não precisa esperar a geração completa terminar.
        const summarySessionIdForThisSend = summarySessionId;
        const needsSummary = !summaryInfo.status || summaryInfo.label === tCommon('new_chat');
        const pendingSummaryPromise = needsSummary
            ? fetch(`${process.env.REACT_APP_API_URL}/post_summary_chat`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${accessToken}`
                },
                body: JSON.stringify({
                    history: auxiliar_message,
                    session_id: summarySessionIdForThisSend,
                    openrouter: useOpenrouter ? 'true' : 'false'
                })
            })
                .then(res => res.json())
                .then(summaryResult => {
                    updateSummary({
                        label: summaryResult.label,
                        status: summaryResult.status
                    });
                    setSessions(prev =>
                        prev.map(sess =>
                            sess.session_id === summarySessionIdForThisSend
                                ? { ...sess, label: summaryResult.label }
                                : sess
                        )
                    );
                    return summaryResult;
                })
                .catch(error => {
                    console.error("Erro ao gerar resumo da sessão:", error);
                    return null;
                })
            : null;

        let filtros;

        if (assistantId === '1') {
            filtros = {
                sectors: filtro.sectors || [],
                subsectors: filtro.subsectors || { building: [], environment: [], transport: [] },
                countries: filtro.countries || []
            };
        } else if (assistantId === '182') {
            filtros = {
                cliente: filtro.cliente || [],
                proposta: filtro.proposta || [],
                area: filtro.area || [],
                servico: filtro.servico || []
            };
        }

        // Decisão de 2026-09-09: o tempo total conta desde o ENVIO da mensagem só
        // quando a skill é via "/" - o pill já nasce nesse instante, então o
        // usuário vê o cronômetro contando desde 0. No chat comum (sem skill via
        // "/") o painel só aparece quando a 1ª ferramenta chega - contar desde o
        // envio aqui faria ele "nascer" já com um número corrido (o tempo que o
        // agente gasta decidindo se vai usar ferramenta), sem o usuário ter visto
        // a contagem subir. Dois critérios diferentes de "tempo total" aceitos
        // conscientemente por esse motivo.
        const skillPillStartedAt = skillCommandText ? Date.now() : null;

        // Atualiza mensagens com a pergunta do usuário e adiciona resposta vazia do assistente
        const assistantPlaceholderClientId = generateClientId();
        const assistantPlaceholder = {
            role: 'assistant',
            content: '',
            clientId: assistantPlaceholderClientId,
            ...(skillCommandText ? {
                activity_step: { type: 'skill', status: 'in_progress', data: { skill_name: skillCommandText.slice(1) } },
                activity_started_at: skillPillStartedAt,
            } : {}),
        };
        setMessages([...auxiliar_message, assistantPlaceholder]);
        setChatHistory([...auxiliar_message, assistantPlaceholder]);

        if (resetFileInput && fileInputRef.current) {
            fileInputRef.current.value = null;
        }
        if (resetSelectedFiles) {
            setSelectedFiles([]);
        }
        if (resetInput) {
            clearMessageInput();
        }
        setIsSending(true);
        setMessageSent(true);
        setActivitySteps(skillCommandText ? [{
            type: 'skill',
            status: 'in_progress',
            data: { skill_name: skillCommandText.slice(1) },
            startedAt: skillPillStartedAt,
        }] : []);
        setActivityPanelOpen(false);
        setActivityPanelMessageId(null);

        // Declarado fora do try (não dentro dele, como as outras variáveis de streaming
        // logo abaixo) porque o `finally` também precisa ler essa flag (rede de
        // segurança de streaming interrompido) - uma `let` dentro de `try { ... }` não é
        // visível em `catch`/`finally`, são blocos separados.
        let doneStepEmitted = false;

        try {
            let resposta;
            if (assistantId === '1') {
                resposta = await fetch(`${process.env.REACT_APP_API_URL}/post_chat_core`, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": `Bearer ${accessToken}`
                    },
                    body: JSON.stringify({
                        'history': auxiliar_message,
                        'model': model,
                        'session_id': sessionId,
                        'email': resolvedUser,
                        'total_cost': totalCost,
                        'filter': filtros,
                        'interaction_id': interactionId || null
                    }),
                });

            }
            else if (assistantId === '2') {
                resposta = await fetch(`${process.env.REACT_APP_API_URL}/chat_sesan`, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": `Bearer ${accessToken}`
                    },
                    body: JSON.stringify({
                        'history': auxiliar_message,
                        'llm': model,
                        'session_id': sessionId,
                        'email': resolvedUser,
                        'total_cost': totalCost,
                        'interaction_id': interactionId || null
                    }),
                });
            }
            else if (assistantId === '0') {
                const resourcesData = resourceGroup?.reduce((acc, resource) => ({
                    geminiKey: resource?.pdf_processing?.key_gemini ?? acc.geminiKey,
                    blobConnection: resource?.pdf_processing?.connection_string ?? acc.blobConnection,
                    blobContainer: resource?.pdf_processing?.container_name ?? acc.blobContainer
                }), {
                    geminiKey: null,
                    blobConnection: null,
                    blobContainer: null
                });

                const metadata = uploadedFiles.map(file => ({
                    file_id: file.file_id,
                    filename: file.filename,
                    blob_name: file.blob_name,
                    session_id: file.session_id,
                    user_id: file.user_id,
                    tokens: file.file_tokens,
                    type: file.type,
                    file_uri: file.file_uri,
                    image_url: file.image_url,
                    expiration: file.expiration,
                    content: file.content,
                    size_bytes: file.size_bytes

                    // outros campos que voc� tenha
                }));

                const formData = new FormData();
                formData.append('query', messageValue || '');
                formData.append('session_id', sessionId);
                formData.append('user', resolvedUser);
                formData.append('llm', effectiveModel);
                formData.append('assistant', assistant);
                formData.append('assistant_id', assistantId);
                formData.append('total_cost', totalCost);
                formData.append('history', JSON.stringify(auxiliar_message));
                formData.append('interaction_id', interactionId || null)
                formData.append('files', JSON.stringify(metadata || []));
                formData.append('resources', JSON.stringify(resourcesData));
                formData.append('openrouter', useOpenrouter ? 'true' : 'false');
                // Tarefa 6.4 (FR-039): sinalizador oculto de "Editar skill no
                // chat" - so vai na requisicao quando a mensagem foi de fato
                // originada por aquela acao, sem aparecer no texto visivel.
                if (sendingSkillEditIntent) {
                    formData.append('intent', 'edit');
                }

                // PBI 872: só o chat_iris (assistant_id '0') vai pro Container App -
                // é a única rota que precisava de timeout longo (30min). O resto da
                // plataforma continua no Web App (REACT_APP_API_URL). Sem
                // REACT_APP_ACA_API_URL configurada, cai de volta pro Web App normal.
                const chatIrisApiUrl = process.env.REACT_APP_ACA_API_URL || process.env.REACT_APP_API_URL;

                resposta = await fetch(`${chatIrisApiUrl}/post_chat_generic`, {
                    method: "POST",
                    headers: {
                        "Authorization": `Bearer ${accessToken}`
                    },
                    body: formData,
                });

                if (resposta.ok) {
                    setUploadedFiles([]);
                }

            }
            else if (assistantId === '3') {

                resposta = await fetch(`${process.env.REACT_APP_API_URL}/post_chat_site`, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": `Bearer ${accessToken}`
                    },
                    body: JSON.stringify({
                        'history': auxiliar_message,
                        'llm': model, // Inclui o modelo llm na requisi��o
                        'session_id': sessionId,
                        'email': resolvedUser,
                        'total_cost': totalCost,
                        'interaction_id': interactionId || null,
                    }),
                });

            } else if (assistantId === '182') {

                const resourcesData = resourceGroup?.reduce((acc, resource) => ({
                    geminiKey: resource?.pdf_processing?.key_gemini ?? acc.geminiKey,
                    blobConnection: resource?.pdf_processing?.connection_string ?? acc.blobConnection,
                    blobContainer: resource?.pdf_processing?.container_name ?? acc.blobContainer
                }), {
                    geminiKey: null,
                    blobConnection: null,
                    blobContainer: null
                });

                const metadata = uploadedFiles.map(file => ({
                    file_id: file.file_id,
                    filename: file.filename,
                    blob_name: file.blob_name,
                    session_id: file.session_id,
                    user_id: file.user_id,
                    tokens: file.tokens,
                    type: file.type,
                    file_uri: file.file_uri,
                    image_url: file.image_url,
                    expiration: file.expiration,
                    content: file.content,
                    size_bytes: file.size_bytes

                    // outros campos que você tenha
                }));
                const formData = new FormData();
                formData.append('session_id', sessionId);
                formData.append('email', user);
                formData.append('model', model);
                formData.append('assistant_id', assistantId);
                formData.append('history', JSON.stringify(auxiliar_message));
                formData.append('interaction_id', interactionId || null);
                formData.append('filter', JSON.stringify(filtros || {}));
                formData.append('files', JSON.stringify(metadata || []));
                formData.append('resources', JSON.stringify(resourcesData));

                resposta = await fetch(`${process.env.REACT_APP_API_URL}/post_chat_propostai`, {
                    method: "POST",
                    headers: {
                        //"Content-Type": "application/json",
                        "Authorization": `Bearer ${accessToken}`
                    },
                    body: formData,
                });

                if (resposta.ok) {
                    setUploadedFiles([]);
                }

            } else if (assistantId === '1118'){
                const formData = new FormData();
                formData.append('session_id', sessionId);
                formData.append('email', user);
                formData.append('model', model);
                formData.append('assistant_id', assistantId);
                formData.append('history', JSON.stringify(auxiliar_message));
                formData.append('interaction_id', interactionId || null);
                //formData.append('files', JSON.stringify(metadata || []));

                resposta = await fetch(`${process.env.REACT_APP_API_URL}/sabesp/perguntar`, {
                    method: "POST",
                    headers: {
                        //"Content-Type": "application/json",
                        "Authorization": `Bearer ${accessToken}`
                    },
                    body: formData,
                });
            }
            
            else {
                const resourcesData = resourceGroup?.reduce((acc, resource) => ({
                    geminiKey: resource?.pdf_processing?.key_gemini ?? acc.geminiKey,
                    blobConnection: resource?.pdf_processing?.connection_string ?? acc.blobConnection,
                    blobContainer: resource?.pdf_processing?.container_name ?? acc.blobContainer,
                    search_key: resource?.self_assistant?.search_key ?? acc.search_key,
                    endpoint: resource?.self_assistant?.endpoint ?? acc.endpoint,
                    index: resource?.self_assistant?.index ?? acc.index,
                    openai_endpoint: resource?.self_assistant?.openai_endpoint ?? acc.openai_endpoint,
                    openai_key: resource?.self_assistant?.api_key ?? acc.api_key
                }), {
                    geminiKey: null,
                    blobConnection: null,
                    blobContainer: null,
                    search_key: null,
                    endpoint: null,
                    index: null,
                    openai_endpoint: null,
                    openai_key: null
                });

                const metadata = uploadedFiles.map(file => ({
                    file_id: file.file_id,
                    filename: file.filename,
                    blob_name: file.blob_name,
                    session_id: file.session_id,
                    user_id: file.user_id,
                    tokens: file.tokens,
                    type: file.type,
                    file_uri: file.file_uri,
                    image_url: file.image_url,
                    expiration: file.expiration,
                    content: file.content,
                    size_bytes: file.size_bytes
                    // outros campos que voc� tenha
                }));
                const formData = new FormData();
                formData.append('session_id', sessionId);
                formData.append('email', resolvedUser);
                formData.append('model', model);
                formData.append('assistant_id', assistantId);
                formData.append('total_cost', totalCost);
                formData.append('history', JSON.stringify(auxiliar_message));
                formData.append('interaction_id', interactionId || null)
                formData.append('filter', JSON.stringify(filtros || {}));
                formData.append('files', JSON.stringify(metadata || []));
                formData.append('resources', JSON.stringify(resourcesData));
                // Tarefa 6.4 (FR-039): mesmo sinalizador oculto do branch de
                // Chat Iris (post_chat_generic) acima - a feature de edicao de
                // skills tambem se aplica a self-assistants publicados
                // (FR-019).
                if (sendingSkillEditIntent) {
                    formData.append('intent', 'edit');
                }

                for (const file of filesInMessage) {
                    formData.append('uploaded_files', file);
                }

                // Mesma razão do chat_iris logo acima (PBI 872): o self_assistant
                // passou a ter as mesmas capacidades longas (skills em sandbox,
                // execução de código, documentos), e o teto de ~5 min do Web App
                // corta essas chamadas no meio. Sem REACT_APP_ACA_API_URL
                // configurada, cai de volta pro Web App normal. O resto da
                // plataforma (config do assistente, upload, histórico) continua
                // em REACT_APP_API_URL.
                const selfAssistantApiUrl = process.env.REACT_APP_ACA_API_URL || process.env.REACT_APP_API_URL;

                resposta = await fetch(`${selfAssistantApiUrl}/post_chat_selfassistant`, {
                    method: "POST",
                    headers: {
                        //"Content-Type": "application/json",
                        "Authorization": `Bearer ${accessToken}`
                    },
                    body: formData,
                });

                if (resposta.ok) {
                    setUploadedFiles([]);
                }

            }



            if (!resposta.body) {
                throw new Error("Resposta vazia do servidor")
            };

            const reader = resposta.body.getReader();
            const decoder = new TextDecoder();
            let streaming = false;
            let lastInteractionId = null;
            let fullContent = "";
            // Trilha de execucao da skill (backend/skills_helpers/execution_events.py).
            // Fora do setState de proposito: o backend manda DOIS eventos por
            // tool (running, depois ok/error) com o MESMO id, e o segundo tem
            // que substituir o primeiro em vez de virar uma linha nova - isso
            // exige ler o array anterior, o que um setState funcional puro nao
            // da' de graca aqui. Mesmo padrao do fullContent logo acima.
            let toolEvents = [];
            // Cada tool_event marca que o texto seguinte começa uma nova narração de
            // passo (ver SKILL.md da fixture /teste-activity-panel: "escreva UMA frase
            // curta... antes de seguir para o próximo passo") - o modelo gera essas
            // frases em rodadas separadas e a próxima rodada não inclui o espaço/quebra
            // de linha antes de si mesma, então sem isso a narração de um passo gruda
            // direto na anterior (ex.: "restantes.A pasta...").
            let pendingParagraphBreak = false;
            // Tarefa 4: fecha a timeline do painel "Atividade" em "Concluído" quando o
            // streaming termina de verdade (parsedData.interaction_id, emitido uma única
            // vez pelo backend depois de todos os chunks/tool_events - controllers.py),
            // e só quando houve tool_event real nesta execução. Fechar no primeiro texto
            // que chegasse depois de qualquer tool_event (versão anterior) quebrava com
            // skills que narram texto ENTRE passos (ex.: fixture /teste-activity-panel) -
            // a timeline travava na 1ª ferramenta porque Chat/index.js:73 passa a usar
            // msg.activity_timeline (congelado) no lugar do state "ao vivo" assim que ele
            // existe, mesmo que a execução real ainda tivesse muitas ferramentas por vir.
            // (doneStepEmitted é declarado antes do try, acima - ver comentário lá.)
            // Nome real da skill (chega junto de cada tool_event - controllers.py). Guardado
            // fora do state porque só é lido no momento de fechar a timeline (abaixo), e
            // setState não é sincrono o bastante pra estar disponível ali.
            let lastSkillName = null;
            // Snapshot da timeline completa desta execução, gravado na própria mensagem
            // (msg.activity_timeline) pro pill dela reabrir a ATIVIDADE DELA, e não sempre a
            // execução mais recente - activitySteps (state) é só a timeline "ao vivo", que o
            // próximo envio reseta.
            let activityTimelineSnapshot = null;
            // Tarefa 5 (extensão): espelha, fora do state, os steps mapeados do fluxo SEM
            // skill (analyzing/writing/preparing_file) - mesmo motivo do toolEvents acima,
            // pra poder fechar o snapshot da timeline sem depender de ler activitySteps
            // (state) de forma síncrona.
            let mainAgentSteps = [];
            // Mesmo motivo do mirror de mainAgentSteps acima, mas para o step "searching"
            // (citações de busca web - web_search_citation): sem isso, uma resposta que só
            // teve busca na web (sem skill nem outra tool) nunca fecha activityTimelineSnapshot
            // lá embaixo, e o pill/resumo persistente nunca aparece na mensagem.
            let searchingStepMirror = null;
            // Tarefa 9 (FR-019): timestamp de quando a 1ª etapa desta execução chegou -
            // guardado fora do state pelo mesmo motivo de lastSkillName acima (setState
            // não é síncrono o bastante pra estar disponível na hora de fechar a timeline).
            // Base do tempo total (relógio de parede, não soma dos `ms` de cada tool).
            // Herda `skillPillStartedAt` quando o pill já nasceu no envio (skill via "/") -
            // senão o cronômetro voltaria pra 0 no instante em que o 1º evento real chega.
            let executionStartedAt = skillPillStartedAt;
            // Tarefa 9 (FR-019): tempo total (relógio de parede) da execução, congelado no
            // instante em que a timeline fecha - usado pelo pill persistente (ActivityStepSummary).
            let executionDurationMs = null;


            while (true) {
                const { done, value } = await reader.read();

                if (done) {
                    break;
                }

                // --- CORRE��O DO BUG: Verifica se a sess�o mudou durante o stream ---
                if (currentSessionRef.current !== sendingSessionId) {
                    // Se o ID da sess�o atual for diferente do ID da sess�o que iniciou o envio,
                    // cancelamos o loop para n�o atualizar o estado do novo chat.
                    // Etapa 4 (PBI 872): o backend continua gerando a resposta mesmo com o
                    // reader cancelado aqui - marca a sess�o como "processing" pra lista de
                    // conversas mostrar o indicador e o Historico saber o que fazer polling.
                    // Se a sess�o ainda n�o existir na lista (chat novo, primeira mensagem,
                    // ainda sem hist�rico salvo no Mongo), cria a entrada em vez de s�
                    // atualizar - sen�o o abandono de um chat novo nunca mostrava indicador
                    // nenhum, porque n�o havia onde marcar o status.
                    reader.cancel();
                    setSessions(prev => {
                        const exists = prev.some(s => s.session_id === sendingSessionId);
                        if (exists) {
                            return prev.map(s =>
                                s.session_id === sendingSessionId ? { ...s, status: "processing" } : s
                            );
                        }
                        return [{ session_id: sendingSessionId, label: '', status: "processing" }, ...prev];
                    });
                    break;
                }
                // --------------------------------------------------------------------

                const chunk = decoder.decode(value, { stream: true });
                if (chunk.includes("data: ")) {
                    streaming = true;
                    // Processa cada linha separadamente
                    const lines = chunk
                        .split('\n')
                        .filter(line => line.startsWith("data: "))
                        .map(line => line.replace("data: ", "")
                            .trim());

                    for (const line of lines) {
                        setIsSending(false);
                        if (line === "[DONE]") {
                            //flushBuffer();
                            break;
                        }
                        try {
                            const parsedData = JSON.parse(line);

                            if (parsedData.error) {
                                console.log('Erro recebido do servidor:', parsedData.error);
                                setAlertTitle(tChat('alertErrorTitle'));
                                setAlertType("error");
                                setAlertMessage(translateBackendError(parsedData.error));
                                setShowAlert(true);
                                setActivitySteps(prev => (
                                    prev.length > 0
                                        ? prev.map(s => {
                                            // Achado 2026-09-10: além da etapa "guarda-chuva" (ex: o
                                            // wrapper 'skill'), as tools INTERNAS dela (s.data.tools -
                                            // ex: "Lendo um arquivo") têm seu próprio status/cronômetro
                                            // e não eram tocadas aqui - continuavam 'in_progress' pra
                                            // sempre, com o tempo daquela linha específica seguindo.
                                            const tools = Array.isArray(s.data?.tools)
                                                ? s.data.tools.map(t => (
                                                    t.status === 'in_progress'
                                                        ? { ...t, status: 'error', durationMs: frozenToolDurationMs({ status: 'error' }, t, t.startedAt) }
                                                        : t
                                                ))
                                                : s.data?.tools;
                                            return s.status === 'in_progress'
                                                ? {
                                                    ...s,
                                                    status: 'error',
                                                    durationMs: frozenStepDurationMs(s, 'error', s.startedAt),
                                                    ...(tools ? { data: { ...s.data, tools } } : {}),
                                                }
                                                : (tools ? { ...s, data: { ...s.data, tools } } : s);
                                        })
                                        : prev
                                ));
                                // `msg.activity_step` (indicador inline dentro da bolha, separado do
                                // painel lateral) também ficava preso em 'in_progress' pra sempre -
                                // congela em 'error' (ícone + "Falha, tente novamente", parado) em vez
                                // de deixar rodando.
                                const congelarActivityStepInline = prev => {
                                    const updated = [...prev];
                                    const lastIdx = updated.length - 1;
                                    if (lastIdx >= 0 && updated[lastIdx].role === 'assistant' && updated[lastIdx].activity_step?.status === 'in_progress') {
                                        const anchor = updated[lastIdx].activity_started_at;
                                        updated[lastIdx] = {
                                            ...updated[lastIdx],
                                            activity_step: {
                                                ...updated[lastIdx].activity_step,
                                                status: 'error',
                                                // Sem isso, useElapsedSeconds (ActivityStepInline) não
                                                // tem `frozenMs` pra congelar de verdade - cai no
                                                // fallback ao vivo mesmo com status 'error' (mesmo
                                                // achado do fix em ActivityStepInline/index.js).
                                                durationMs: typeof anchor === 'number' ? Date.now() - anchor : undefined,
                                            },
                                        };
                                    }
                                    return updated;
                                };
                                setMessages(congelarActivityStepInline);
                                setChatHistory(congelarActivityStepInline);

                                // Achado 2026-09-10 (ponto 3): sem `activity_summary.duration_ms`, o
                                // tempo TOTAL do painel (ActivityPanel-title, lido de
                                // `activityPanelMessage?.activity_summary?.duration_ms` em
                                // Chat/index.js) não tem valor congelado pra usar - mesmo com toda
                                // etapa já parada, ele recalculava `Date.now() - início` a cada
                                // re-render por qualquer outro motivo, então parecia nunca travar.
                                const totalDurationMsNoErro = executionStartedAt !== null ? Date.now() - executionStartedAt : undefined;
                                const skillNameFinal = lastSkillName || (skillCommandText ? skillCommandText.slice(1) : null);
                                const gravarActivitySummary = prev => {
                                    const updated = [...prev];
                                    const lastIdx = updated.length - 1;
                                    if (lastIdx >= 0 && updated[lastIdx].role === 'assistant') {
                                        updated[lastIdx] = {
                                            ...updated[lastIdx],
                                            activity_summary: { skill_name: skillNameFinal, duration_ms: totalDurationMsNoErro },
                                        };
                                    }
                                    return updated;
                                };
                                setMessages(gravarActivitySummary);
                                setChatHistory(gravarActivitySummary);

                                // Achado 2026-09-10 (ponto 5): a sessão ficava presa em "Novo Chat"
                                // pra sempre em qualquer interação que terminasse em erro - a
                                // persistência do título (2ª chamada a /post_summary_chat, COM
                                // interaction_id) só rodava no fechamento de SUCESSO
                                // (parsedData.interaction_id, abaixo), nunca alcançado aqui. O
                                // backend agora manda `interaction_id` também no payload de erro
                                // (controllers.py::_abortar) pra isso ter como persistir.
                                if (pendingSummaryPromise && parsedData.interaction_id) {
                                    try {
                                        const summaryResult = await pendingSummaryPromise;
                                        if (summaryResult) {
                                            fetch(`${process.env.REACT_APP_API_URL}/post_summary_chat`, {
                                                method: "POST",
                                                headers: {
                                                    "Content-Type": "application/json",
                                                    "Authorization": `Bearer ${accessToken}`
                                                },
                                                body: JSON.stringify({
                                                    interaction_id: parsedData.interaction_id,
                                                    session_id: summarySessionIdForThisSend,
                                                    label: summaryResult.label,
                                                    openrouter: useOpenrouter ? 'true' : 'false'
                                                })
                                            }).catch(error => {
                                                console.error("Erro ao persistir label da sessão (caminho de erro):", error);
                                            });
                                        }
                                    } catch (error) {
                                        console.error("Erro ao aguardar resumo da sessão (caminho de erro):", error);
                                    }
                                }

                                reader.cancel();
                                break;
                            }

                            if (parsedData.interaction_id) {
                                const currentInteractionId = parsedData.interaction_id;
                                setInteractionId(currentInteractionId);

                                // Fecha a timeline do painel "Atividade" aqui - interaction_id só
                                // chega uma vez, depois de TODOS os chunks/tool_events desta
                                // execução (controllers.py), diferente da versão anterior que fechava
                                // no primeiro chunk de texto depois de qualquer tool_event (quebrava
                                // com skills que narram entre passos - ver comentário de
                                // doneStepEmitted acima).
                                // `|| skillCommandText`: skill via "/" que respondeu só com texto
                                // (ou falhou) sem chamar nenhuma tool interna nunca gera um
                                // tool_event - sem esse OU, o step otimista semeado no envio (linha
                                // ~863, status 'in_progress') nunca fechava aqui, e o cronômetro +
                                // as animações (shimmer, bolinhas) ficavam rodando pra sempre, mesmo
                                // com a resposta já toda exibida (achado 2026-09-10, bug ao vivo).
                                if (!doneStepEmitted && (toolEvents.length > 0 || skillCommandText)) {
                                    doneStepEmitted = true;
                                    if (!lastSkillName && skillCommandText) {
                                        lastSkillName = skillCommandText.slice(1);
                                    }
                                    executionDurationMs = executionStartedAt !== null ? Date.now() - executionStartedAt : null;
                                    // Cópia local do total: o valor é lido dentro de callbacks de
                                    // setState, e a variável de fora do loop muda a cada execução.
                                    const totalDurationMs = executionDurationMs ?? undefined;

                                    // Tarefa 5 (extensão): sem skill_name, não há nome de
                                    // skill pra fechar num pill "Acionou skill X" - o
                                    // snapshot usa os steps mapeados (mainAgentSteps) em vez
                                    // do wrapper de skill, e o pill (ActivityStepSummary)
                                    // cai no texto genérico quando summary.skill_name falta.
                                    // Tarefa 9 (FR-019): o snapshot é lido pelo painel depois, sem
                                    // relógio pra recalcular nada, então cada linha leva a própria
                                    // duração já congelada - e o wrapper da skill leva o total da
                                    // execução, que é o que o cabeçalho do painel mostra.
                                    if (lastSkillName) {
                                        const finalTools = toolEvents.map(evento => ({
                                            tool_id: evento.id,
                                            tool_label: tChat(`executionTrail.tools.${evento.name}`, { defaultValue: evento.label || evento.name }),
                                            status: evento.status === 'running'
                                                ? 'in_progress'
                                                : (evento.status === 'error' ? 'error' : 'completed'),
                                            durationMs: typeof evento.ms === 'number' ? evento.ms : undefined,
                                        }));
                                        activityTimelineSnapshot = [
                                            {
                                                type: 'skill',
                                                status: 'completed',
                                                data: { skill_name: lastSkillName, tools: finalTools },
                                                durationMs: totalDurationMs,
                                            },
                                            { type: 'done', status: 'completed', data: {} },
                                        ];
                                    } else if (mainAgentSteps.length > 0) {
                                        activityTimelineSnapshot = [
                                            ...mainAgentSteps.map(s => ({
                                                ...s,
                                                status: 'completed',
                                                durationMs: frozenStepDurationMs(s, 'completed', s.startedAt),
                                            })),
                                            { type: 'done', status: 'completed', data: {} },
                                        ];
                                    } else if (searchingStepMirror) {
                                        activityTimelineSnapshot = [
                                            {
                                                ...searchingStepMirror,
                                                durationMs: frozenStepDurationMs(searchingStepMirror, 'completed', searchingStepMirror.startedAt),
                                            },
                                            { type: 'done', status: 'completed', data: {} },
                                        ];
                                    }

                                    // Etapa ainda "em andamento" quando a execução termina fica
                                    // concluída aqui - senão o cronômetro dela seguiria contando
                                    // com a execução já terminada.
                                    setActivitySteps(prev => (
                                        prev.length > 0
                                            ? [
                                                ...prev.map(s => (
                                                    s.status === 'in_progress'
                                                        ? {
                                                            ...s,
                                                            status: 'completed',
                                                            durationMs: s.type === 'skill'
                                                                ? totalDurationMs
                                                                : frozenStepDurationMs(s, 'completed', s.startedAt),
                                                        }
                                                        : s
                                                )),
                                                { type: 'done', status: 'completed', data: {} },
                                            ]
                                            : prev
                                    ));
                                }

                                setMessages(prevMessages => {
                                    const updatedMessages = [...prevMessages];
                                    const lastMsgIndex = updatedMessages.length - 1;
                                    if (lastMsgIndex >= 0) {
                                        updatedMessages[lastMsgIndex] = {
                                            ...updatedMessages[lastMsgIndex],
                                            _id: currentInteractionId,
                                            activity_step: null,
                                            ...(activityTimelineSnapshot ? {
                                                // Pill persistente (Tarefa 4): único jeito de reabrir o
                                                // painel "Atividade" depois que a resposta final já
                                                // apareceu, já que o indicador inline some aqui.
                                                activity_summary: { skill_name: lastSkillName, duration_ms: executionDurationMs },
                                                activity_timeline: activityTimelineSnapshot,
                                            } : {}),
                                        };
                                    }
                                    return updatedMessages;
                                });

                                setChatHistory(prevMessages => {
                                    const updatedMessages = [...prevMessages];
                                    const lastMsgIndex = updatedMessages.length - 1;
                                    if (lastMsgIndex >= 0) {
                                        updatedMessages[lastMsgIndex] = {
                                            ...updatedMessages[lastMsgIndex],
                                            _id: currentInteractionId,
                                            activity_step: null,
                                            ...(activityTimelineSnapshot ? {
                                                activity_summary: { skill_name: lastSkillName, duration_ms: executionDurationMs },
                                                activity_timeline: activityTimelineSnapshot,
                                            } : {}),
                                        };
                                    }
                                    return updatedMessages;
                                });

                                // Tempo total do Painel de Atividade (Tarefa 9, FR-019): quem mediu
                                // certo foi o PRÓPRIO NAVEGADOR (executionDurationMs, calculado acima
                                // em doneStepEmitted) - decisão de 2026-09-09, depois de duas tentativas
                                // de fazer o backend reproduzir esse relógio sozinho (sempre sobrava uma
                                // variação de rede entre os dois relógios). Em vez disso, o navegador
                                // avisa o backend do valor já certo, pra persistir como está - sem
                                // isso, o histórico recarregado cai na aproximação por soma de `ms`
                                // (buildActivityFromExecutionEvents.js). Fire-and-forget, mesmo padrão
                                // do post_summary_chat abaixo.
                                if (typeof executionDurationMs === 'number') {
                                    fetch(`${process.env.REACT_APP_API_URL}/post_activity_duration`, {
                                        method: "POST",
                                        headers: {
                                            "Content-Type": "application/json",
                                            "Authorization": `Bearer ${accessToken}`
                                        },
                                        body: JSON.stringify({
                                            interaction_id: currentInteractionId,
                                            activity_duration_ms: executionDurationMs,
                                        })
                                    }).catch(error => {
                                        console.error("Erro ao persistir tempo total da atividade:", error);
                                    });
                                }

                                // Persiste na interação recém-criada o título que já foi gerado
                                // (e já aplicado na UI) em paralelo, lá no início do envio - não
                                // repete a chamada de LLM, só grava o label já pronto.
                                if (pendingSummaryPromise) {
                                    try {
                                        const summaryResult = await pendingSummaryPromise;
                                        if (summaryResult) {
                                            fetch(`${process.env.REACT_APP_API_URL}/post_summary_chat`, {
                                                method: "POST",
                                                headers: {
                                                    "Content-Type": "application/json",
                                                    "Authorization": `Bearer ${accessToken}`
                                                },
                                                body: JSON.stringify({
                                                    interaction_id: currentInteractionId,
                                                    session_id: summarySessionIdForThisSend,
                                                    label: summaryResult.label,
                                                    openrouter: useOpenrouter ? 'true' : 'false'
                                                })
                                            }).catch(error => {
                                                console.error("Erro ao persistir label da sess�o:", error);
                                            });
                                        }
                                    } catch (error) {
                                        console.error("Erro ao aguardar resumo da sess�o:", error);
                                    }
                                }
                            }

                            if (parsedData.activity_step) {
                                if (executionStartedAt === null) {
                                    executionStartedAt = Date.now();
                                }
                                const stepPatch = { activity_step: parsedData.activity_step, activity_started_at: executionStartedAt };

                                setActivitySteps(prev => {
                                    // Etapas 'writing' são chaveadas por type+output_format: FR-008/multi-arquivo
                                    // precisa de uma linha por arquivo gerado, não de uma só linha sobrescrita.
                                    const stepKey = (s) => (s.type === 'writing' ? `writing:${s.data?.output_format || ''}` : s.type);
                                    const idx = prev.findIndex(s => stepKey(s) === stepKey(parsedData.activity_step));
                                    // Tarefa 9 (FR-019): startedAt preservado entre atualizações da MESMA
                                    // etapa (senão o cronômetro individual reiniciaria a cada patch).
                                    const stepStartedAt = idx >= 0 ? prev[idx].startedAt : Date.now();
                                    // Tarefa 9 (FR-019): duração congelada quando a etapa sai de "em
                                    // andamento" - sem isso o painel recalcula Date.now() - startedAt a
                                    // cada novo evento e a linha já concluída volta a crescer.
                                    const newStep = {
                                        ...parsedData.activity_step,
                                        startedAt: stepStartedAt,
                                        durationMs: frozenStepDurationMs(
                                            idx >= 0 ? prev[idx] : null,
                                            parsedData.activity_step.status,
                                            stepStartedAt,
                                        ),
                                    };
                                    const updated = idx >= 0
                                        ? prev.map((s, i) => (i === idx ? newStep : s))
                                        : [...prev, newStep];

                                    return updated;
                                });

                                setMessages(prev => {
                                    const updated = [...prev];
                                    const lastIdx = updated.length - 1;
                                    // Sempre mescla na ÚLTIMA mensagem do assistente deste turno (nunca
                                    // abre uma bolha nova por causa de já ter texto) - fullContent, escrito
                                    // pelo handler de parsedData.chunk, é o texto acumulado da resposta
                                    // INTEIRA, não só desta bolha; abrir uma bolha nova a cada novo
                                    // indicador de etapa fazia esse texto completo se repetir em cada
                                    // bolha subsequente (bug real: resposta "fragmentada" em vários blocos
                                    // de texto duplicado sempre que a skill narra entre passos).
                                    if (lastIdx >= 0 && updated[lastIdx].role === 'assistant') {
                                        updated[lastIdx] = { ...updated[lastIdx], ...stepPatch };
                                    } else {
                                        updated.push({ role: 'assistant', content: '', ...stepPatch, clientId: generateClientId() });
                                    }
                                    return updated;
                                });

                                setChatHistory(prev => {
                                    const updated = [...prev];
                                    const lastIdx = updated.length - 1;
                                    // Sempre mescla na ÚLTIMA mensagem do assistente deste turno (nunca
                                    // abre uma bolha nova por causa de já ter texto) - fullContent, escrito
                                    // pelo handler de parsedData.chunk, é o texto acumulado da resposta
                                    // INTEIRA, não só desta bolha; abrir uma bolha nova a cada novo
                                    // indicador de etapa fazia esse texto completo se repetir em cada
                                    // bolha subsequente (bug real: resposta "fragmentada" em vários blocos
                                    // de texto duplicado sempre que a skill narra entre passos).
                                    if (lastIdx >= 0 && updated[lastIdx].role === 'assistant') {
                                        updated[lastIdx] = { ...updated[lastIdx], ...stepPatch };
                                    } else {
                                        updated.push({ role: 'assistant', content: '', ...stepPatch, clientId: generateClientId() });
                                    }
                                    return updated;
                                });
                            }

                            if (parsedData.tool_event) {
                                //console.log('parsedData.tool_event', parsedData.tool_event);
                                if (executionStartedAt === null) {
                                    executionStartedAt = Date.now();
                                }
                                const evento = parsedData.tool_event;
                                lastSkillName = parsedData.skill_name || lastSkillName;
                                pendingParagraphBreak = true;
                                const idx = toolEvents.findIndex(e => e.id === evento.id);
                                if (idx >= 0) {
                                    toolEvents[idx] = evento;
                                } else {
                                    toolEvents.push(evento);
                                }

                                // Copia nova a cada evento: mutar o array no
                                // lugar nao mudaria a referencia e o React nao
                                // re-renderizaria a trilha.
                                const trailPatch = { execution_events: [...toolEvents] };
                                const aplicarTrilha = prev => {
                                    const updated = [...prev];
                                    const lastIdx = updated.length - 1;
                                    if (lastIdx >= 0 && updated[lastIdx].role === 'assistant') {
                                        updated[lastIdx] = { ...updated[lastIdx], ...trailPatch };
                                    } else {
                                        updated.push({
                                            role: 'assistant',
                                            content: '',
                                            _id: lastInteractionId || null,
                                            clientId: generateClientId(),
                                            ...trailPatch
                                        });
                                    }
                                    return updated;
                                };
                                setMessages(aplicarTrilha);
                                setChatHistory(aplicarTrilha);

                                if (parsedData.skill_name) {
                                    // Tarefa 4 (Opção 3): um único step fixo "Acionando skill
                                    // <nome>" (nome real vem de parsedData.skill_name, que anda
                                    // ao lado do tool_event - ver controllers.py), com a lista de
                                    // tools internas como detalhe dentro de data.tools.
                                    setActivitySteps(prev => {
                                        const idx = prev.findIndex(s => s.type === 'skill');
                                        const prevTools = idx >= 0 ? (prev[idx].data?.tools || []) : [];
                                        const toolIdx = prevTools.findIndex(t => t.tool_id === evento.id);
                                        // Tarefa 9 (FR-019): startedAt individual de cada tool interna e
                                        // do step "skill" em si, preservados entre atualizações.
                                        const toolStartedAt = toolIdx >= 0 ? prevTools[toolIdx].startedAt : Date.now();
                                        const toolEntry = {
                                            tool_id: evento.id,
                                            // Mesma cadeia label -> i18n por name do ExecutionTrail (ver
                                            // comentário no topo de components/ExecutionTrail/index.js):
                                            // sem isso a lista de tools da skill aparecia com o nome cru
                                            // (write_todos, read_file...) ou o docstring cru das tools nativas.
                                            tool_label: tChat(`executionTrail.tools.${evento.name}`, { defaultValue: evento.label || evento.name }),
                                            status: evento.status === 'running'
                                                ? 'in_progress'
                                                : (evento.status === 'error' ? 'error' : 'completed'),
                                            startedAt: toolStartedAt,
                                            durationMs: frozenToolDurationMs(
                                                evento,
                                                toolIdx >= 0 ? prevTools[toolIdx] : null,
                                                toolStartedAt,
                                            ),
                                        };
                                        const tools = toolIdx >= 0
                                            ? prevTools.map((t, i) => (i === toolIdx ? toolEntry : t))
                                            : [...prevTools, toolEntry];

                                        const skillStep = {
                                            type: 'skill',
                                            status: 'in_progress',
                                            data: { skill_name: parsedData.skill_name, tools },
                                            startedAt: idx >= 0 ? prev[idx].startedAt : Date.now(),
                                        };

                                        const updated = idx >= 0
                                            ? prev.map((s, i) => (i === idx ? skillStep : s))
                                            : [...prev, skillStep];

                                        if (prev.length === 0) {
                                            // assistantPlaceholderClientId é o mesmo id do placeholder
                                            // criado no início do envio (linha ~661) - a mensagem desta
                                            // resposta preserva esse clientId o turno inteiro, então já
                                            // dá pra "prender" o painel nela desde a primeira tool_event,
                                            // mesmo sem abrir sozinho (só por clique no indicador/pill).
                                            setActivityPanelMessageId(assistantPlaceholderClientId);
                                        }

                                        return updated;
                                    });

                                    const toolStepPatch = {
                                        activity_step: {
                                            type: 'skill',
                                            status: 'in_progress',
                                            data: { skill_name: parsedData.skill_name },
                                        },
                                        activity_started_at: executionStartedAt,
                                    };

                                    const aplicarStepInline = prev => {
                                        const updated = [...prev];
                                        const lastIdx = updated.length - 1;
                                        // Sempre mescla na ÚLTIMA mensagem do assistente deste turno (nunca
                                        // abre uma bolha nova por causa de já ter texto) - fullContent,
                                        // escrito pelo handler de parsedData.chunk, é o texto acumulado da
                                        // resposta INTEIRA, não só desta bolha; abrir uma bolha nova a cada
                                        // novo indicador de etapa fazia esse texto completo se repetir em
                                        // cada bolha subsequente (bug real: resposta "fragmentada" em vários
                                        // blocos de texto duplicado sempre que a skill narra entre passos).
                                        if (lastIdx >= 0 && updated[lastIdx].role === 'assistant') {
                                            updated[lastIdx] = { ...updated[lastIdx], ...toolStepPatch };
                                        } else {
                                            updated.push({ role: 'assistant', content: '', ...toolStepPatch, clientId: generateClientId() });
                                        }
                                        return updated;
                                    };
                                    setMessages(aplicarStepInline);
                                    setChatHistory(aplicarStepInline);
                                } else if (evento.name === 'web_search_citation') {
                                    // Busca na web (PBI 986/Task 1132): cada citação chega como um
                                    // tool_event separado (backend/agents.py::_build_citation_event),
                                    // mas todas se acumulam num ÚNICO step "searching" - mesmo padrão
                                    // usado pela lista data.tools do step "skill" acima, só que aqui
                                    // é data.sites (ver ActivityPanel/index.js, que já tinha a UI de
                                    // chips de site pronta, só faltando este evento de verdade).
                                    // `url` (Tarefa 1, specs/front_planning_tools_ajustes_finais/plan.md,
                                    // FR-002): mesmo campo que backend/agents.py::_build_citation_event
                                    // agora manda, dedicado pra sobreviver à persistência (`output` é
                                    // removido de propósito antes de gravar no Mongo).
                                    let domain = evento.url || '';
                                    try {
                                        domain = new URL(evento.url).hostname;
                                    } catch {
                                        // URL inesperada (não deveria acontecer - backend só manda
                                        // citação com url válida) - mantém o valor cru como domain.
                                    }

                                    // Achado 2026-09-15 (specs/front_planning_tools_ajustes_finais/
                                    // plan.md, Tarefa 1): `searchingStepMirror` precisa ser
                                    // acumulado ANTES/FORA do callback de `setActivitySteps`, igual
                                    // ao `mainAgentSteps` (mutação direta, sem depender de `prev`) -
                                    // senão, se 2+ citações chegarem no mesmo chunk de streaming
                                    // (processadas no mesmo laço síncrono, sem `await` entre elas),
                                    // o React só executa o callback de `setState` de forma síncrona
                                    // pra primeira chamada da sequência; as seguintes ficam na fila e
                                    // só rodam no próximo ciclo - se o fechamento da execução ler o
                                    // mirror antes disso, pegava só a 1ª citação.
                                    const prevMirrorSites = searchingStepMirror?.data?.sites || [];
                                    const mirrorSiteIdx = prevMirrorSites.findIndex(s => s.url === evento.url);
                                    const siteEntry = { domain, url: evento.url, title: evento.label };
                                    const mirrorSites = mirrorSiteIdx >= 0
                                        ? prevMirrorSites.map((s, i) => (i === mirrorSiteIdx ? siteEntry : s))
                                        : [...prevMirrorSites, siteEntry];
                                    searchingStepMirror = {
                                        type: 'searching',
                                        status: 'completed',
                                        data: { sites: mirrorSites, site_count: mirrorSites.length },
                                        startedAt: searchingStepMirror ? searchingStepMirror.startedAt : Date.now(),
                                    };

                                    setActivitySteps(prev => {
                                        const idx = prev.findIndex(s => s.type === 'searching');
                                        const updated = idx >= 0
                                            ? prev.map((s, i) => (i === idx ? searchingStepMirror : s))
                                            : [...prev, searchingStepMirror];

                                        if (prev.length === 0) {
                                            setActivityPanelMessageId(assistantPlaceholderClientId);
                                        }

                                        return updated;
                                    });

                                    const searchStepPatch = {
                                        activity_step: {
                                            type: 'searching',
                                            status: 'completed',
                                            data: { sites: [{ domain, url: evento.url, title: evento.label }], site_count: 1 },
                                        },
                                        activity_started_at: executionStartedAt,
                                    };
                                    const aplicarSearchStepInline = prev => {
                                        const updated = [...prev];
                                        const lastIdx = updated.length - 1;
                                        // Sempre mescla na ÚLTIMA mensagem do assistente deste turno (nunca
                                        // abre uma bolha nova por causa de já ter texto) - fullContent,
                                        // escrito pelo handler de parsedData.chunk, é o texto acumulado da
                                        // resposta INTEIRA, não só desta bolha; abrir uma bolha nova a cada
                                        // novo indicador de etapa fazia esse texto completo se repetir em
                                        // cada bolha subsequente (bug real: resposta "fragmentada" em vários
                                        // blocos de texto duplicado sempre que a skill narra entre passos).
                                        if (lastIdx >= 0 && updated[lastIdx].role === 'assistant') {
                                            updated[lastIdx] = { ...updated[lastIdx], ...searchStepPatch };
                                        } else {
                                            updated.push({ role: 'assistant', content: '', ...searchStepPatch, clientId: generateClientId() });
                                        }
                                        return updated;
                                    };
                                    setMessages(aplicarSearchStepInline);
                                    setChatHistory(aplicarSearchStepInline);
                                } else {
                                    // Tarefa 5 (revisado): tool_event SEM skill_name (agente
                                    // principal do chat, fora de qualquer skill - backend/agents.py).
                                    // Cada chamada vira sua PRÓPRIA linha (type 'tool', mesmo formato
                                    // usado dentro de data.tools de uma skill), em vez de agrupar por
                                    // tipo de ferramenta (writing/analyzing/preparing_file) - agrupar
                                    // escondia quantas vezes o agente rodou create_document/
                                    // executar_codigo/etc (decisão de 2026-09-09, depois de um teste
                                    // real mostrar 2 chamadas de executar_codigo colapsando numa linha só).
                                    const mainAgentIdx = mainAgentSteps.findIndex(s => s.data?.tool_id === evento.id);
                                    // Tarefa 9 (FR-019): startedAt preservado entre atualizações da mesma etapa.
                                    const mappedStartedAt = mainAgentIdx >= 0 ? mainAgentSteps[mainAgentIdx].startedAt : Date.now();
                                    const mappedStep = {
                                        type: 'tool',
                                        status: evento.status === 'running'
                                            ? 'in_progress'
                                            : (evento.status === 'error' ? 'error' : 'completed'),
                                        data: {
                                            tool_id: evento.id,
                                            // Mesma chave label -> i18n por name que o modo skill usa
                                            // (executionTrail.tools.<name>) - sem isso essa linha
                                            // apareceria com o nome técnico cru da tool.
                                            tool_label: tChat(`executionTrail.tools.${evento.name}`, { defaultValue: evento.label || evento.name }),
                                        },
                                        startedAt: mappedStartedAt,
                                        durationMs: frozenToolDurationMs(
                                            evento,
                                            mainAgentIdx >= 0 ? mainAgentSteps[mainAgentIdx] : null,
                                            mappedStartedAt,
                                        ),
                                    };
                                    if (mainAgentIdx >= 0) {
                                        mainAgentSteps[mainAgentIdx] = mappedStep;
                                    } else {
                                        mainAgentSteps.push(mappedStep);
                                    }

                                    setActivitySteps(prev => {
                                        const idx = prev.findIndex(s => s.data?.tool_id === evento.id);
                                        const updated = idx >= 0
                                            ? prev.map((s, i) => (i === idx ? mappedStep : s))
                                            : [...prev, mappedStep];

                                        if (prev.length === 0) {
                                            setActivityPanelMessageId(assistantPlaceholderClientId);
                                        }

                                        return updated;
                                    });

                                    const mappedStepPatch = { activity_step: mappedStep, activity_started_at: executionStartedAt };
                                    const aplicarMappedStepInline = prev => {
                                        const updated = [...prev];
                                        const lastIdx = updated.length - 1;
                                        // Sempre mescla na ÚLTIMA mensagem do assistente deste turno (nunca
                                        // abre uma bolha nova por causa de já ter texto) - fullContent,
                                        // escrito pelo handler de parsedData.chunk, é o texto acumulado da
                                        // resposta INTEIRA, não só desta bolha; abrir uma bolha nova a cada
                                        // novo indicador de etapa fazia esse texto completo se repetir em
                                        // cada bolha subsequente (bug real: resposta "fragmentada" em vários
                                        // blocos de texto duplicado sempre que a skill narra entre passos).
                                        if (lastIdx >= 0 && updated[lastIdx].role === 'assistant') {
                                            updated[lastIdx] = { ...updated[lastIdx], ...mappedStepPatch };
                                        } else {
                                            updated.push({ role: 'assistant', content: '', ...mappedStepPatch, clientId: generateClientId() });
                                        }
                                        return updated;
                                    };
                                    setMessages(aplicarMappedStepInline);
                                    setChatHistory(aplicarMappedStepInline);
                                }
                            }

                            if (parsedData.chunk !== undefined && parsedData.chunk !== null && parsedData.chunk !== '') {
                                const chunkToAppend = parsedData.chunk;

                                setIsTyping(true);

                                // Se o chunk ainda n�o foi adicionado ao conte�do
                                if (!fullContent.endsWith(chunkToAppend)) {
                                    if (pendingParagraphBreak && fullContent !== '' && !/\s$/.test(fullContent)) {
                                        fullContent += '\n\n';
                                    }
                                    pendingParagraphBreak = false;
                                    fullContent += chunkToAppend;

                                    // O fechamento da timeline do painel "Atividade" (doneStepEmitted)
                                    // não acontece mais aqui - foi pro handler de
                                    // parsedData.interaction_id (mais abaixo), que só dispara quando o
                                    // streaming termina de verdade. Ver comentário de doneStepEmitted,
                                    // acima no início deste bloco de streaming.

                                    const fallbackAssistantClientId = generateClientId();

                                    setMessages(prevMessages => {
                                        const updatedMessages = [...prevMessages];
                                        const lastMsgIndex = updatedMessages.length - 1;

                                        if (updatedMessages.length === 0 || updatedMessages[lastMsgIndex].role !== 'assistant') {
                                            updatedMessages.push({ role: 'assistant', content: chunkToAppend, _id: lastInteractionId || null, clientId: fallbackAssistantClientId });
                                        } else {
                                            updatedMessages[lastMsgIndex].content = fullContent;
                                            // NÃO zera activity_step aqui - a execução ainda está em
                                            // andamento (o fechamento real só acontece no handler de
                                            // parsedData.interaction_id, mais abaixo, quando o streaming
                                            // termina de verdade). Zerar a cada chunk de texto fazia o
                                            // indicador ao vivo (ActivityStepInline, Message/index.js)
                                            // sumir e reaparecer entre cada pedaço de texto narrado.

                                            if (!updatedMessages[lastMsgIndex]._id && lastInteractionId) {
                                                updatedMessages[lastMsgIndex]._id = lastInteractionId;
                                            }
                                        }

                                        return updatedMessages;
                                    });

                                    setChatHistory(prevMessages => {
                                        const updatedMessages = [...prevMessages];
                                        const lastMsgIndex = updatedMessages.length - 1;

                                        if (updatedMessages.length === 0 || updatedMessages[lastMsgIndex].role !== 'assistant') {
                                            updatedMessages.push({ role: 'assistant', content: chunkToAppend, _id: lastInteractionId || null, clientId: fallbackAssistantClientId });
                                        } else {
                                            updatedMessages[lastMsgIndex].content = fullContent;
                                            // NÃO zera activity_step aqui - a execução ainda está em
                                            // andamento (o fechamento real só acontece no handler de
                                            // parsedData.interaction_id, mais abaixo, quando o streaming
                                            // termina de verdade). Zerar a cada chunk de texto fazia o
                                            // indicador ao vivo (ActivityStepInline, Message/index.js)
                                            // sumir e reaparecer entre cada pedaço de texto narrado.

                                            if (!updatedMessages[lastMsgIndex]._id && lastInteractionId) {
                                                updatedMessages[lastMsgIndex]._id = lastInteractionId;
                                            }
                                        }

                                        return updatedMessages;
                                    });
                                }
                            }

                            if (parsedData.n8n_pending) {
                                const pendingMsg = {
                                    role: 'assistant',
                                    content: '',
                                    type: 'n8n_confirmation',
                                    n8n_pending: parsedData.n8n_pending,
                                    clientId: generateClientId()
                                };
                                setMessages(prev => {
                                    const updated = [...prev];
                                    const lastIdx = updated.length - 1;
                                    // Card de confirmação n8n substitui a bolha INTEIRA (não é um
                                    // indicador de progresso incremental como activity_step/tool_event) -
                                    // só reaproveita a bolha quando ela ainda está vazia, senão apagaria
                                    // texto real já narrado nela.
                                    if (lastIdx >= 0 && updated[lastIdx].role === 'assistant' && !updated[lastIdx].content) {
                                        updated[lastIdx] = pendingMsg;
                                    } else {
                                        updated.push(pendingMsg);
                                    }
                                    return updated;
                                });
                                setChatHistory(prev => {
                                    const updated = [...prev];
                                    const lastIdx = updated.length - 1;
                                    // Card de confirmação n8n substitui a bolha INTEIRA (não é um
                                    // indicador de progresso incremental como activity_step/tool_event) -
                                    // só reaproveita a bolha quando ela ainda está vazia, senão apagaria
                                    // texto real já narrado nela.
                                    if (lastIdx >= 0 && updated[lastIdx].role === 'assistant' && !updated[lastIdx].content) {
                                        updated[lastIdx] = pendingMsg;
                                    } else {
                                        updated.push(pendingMsg);
                                    }
                                    return updated;
                                });
                            }

                            if (parsedData.skill_created) {
                                const skillCreatedPatch = {
                                    role: 'assistant',
                                    content: fullContent,
                                    type: 'skill_created',
                                    skill_created: parsedData.skill_created,
                                    _id: lastInteractionId || null
                                };
                                setMessages(prev => {
                                    const updated = [...prev];
                                    const lastIdx = updated.length - 1;
                                    if (lastIdx >= 0 && updated[lastIdx].role === 'assistant') {
                                        updated[lastIdx] = { ...updated[lastIdx], ...skillCreatedPatch };
                                    } else {
                                        updated.push(skillCreatedPatch);
                                    }
                                    return updated;
                                });
                                setChatHistory(prev => {
                                    const updated = [...prev];
                                    const lastIdx = updated.length - 1;
                                    if (lastIdx >= 0 && updated[lastIdx].role === 'assistant') {
                                        updated[lastIdx] = { ...updated[lastIdx], ...skillCreatedPatch };
                                    } else {
                                        updated.push(skillCreatedPatch);
                                    }
                                    return updated;
                                });
                            }

                            if (parsedData.history) {
                                setMessages(parsedData.history);
                                setChatHistory(parsedData.history);
                            }

                            if (parsedData.total_cost) {
                                setTotalCost(parsedData.total_cost);
                                setCost(parsedData.total_cost.toFixed(2));
                            }

                            if (parsedData.link) {
                                const newLinks = parsedData.link;
                                setResponseLinks(newLinks);
                                setLinks(newLinks);
                                setMessages(prevMessages => {
                                    const updatedMessages = [...prevMessages];
                                    const lastIdx = updatedMessages.length - 1;
                                    if (lastIdx >= 0 && newLinks.length > 0) {
                                        updatedMessages[lastIdx] = {
                                            ...updatedMessages[lastIdx],
                                            links: newLinks
                                        };
                                    }
                                    return updatedMessages;
                                });
                                setChatHistory(prevMessages => {
                                    const updatedMessages = [...prevMessages];
                                    const lastIdx = updatedMessages.length - 1;
                                    if (lastIdx >= 0 && newLinks.length > 0) {
                                        updatedMessages[lastIdx] = {
                                            ...updatedMessages[lastIdx],
                                            links: newLinks
                                        };
                                    }
                                    return updatedMessages;
                                });

                            }




                        } catch (err) {
                            setAlertTitle(tChat('alertErrorTitle'));
                            setAlertType("error")
                            setAlertMessage(tChat('erro_resposta'))
                            setShowAlert(true);
                            console.error("Erro ao processar chunk:", chunk);
                        }
                    }
                }
            }

        } catch (error) {
            console.error('Erro ao se comunicar com o servidor:', error);
            setMessages(prevMessages => [...prevMessages, { role: 'assistant', content: tChat('serverCommunicationError'), clientId: generateClientId() }]);
        } finally {
            setIsSending(false);
            // Rede de segurança (achado 2026-09-10): se o streaming terminar sem o
            // interaction_id chegar (conexão caiu, exceção antes do fim), o fechamento
            // de cima (doneStepEmitted) nunca roda - sem isso, qualquer etapa ainda "em
            // andamento" fica presa pra sempre, com cronômetro e animação rodando
            // indefinidamente mesmo a mensagem já tendo parado de chegar.
            if (!doneStepEmitted) {
                setActivitySteps(prev => (
                    prev.length > 0
                        ? prev.map(s => (
                            s.status === 'in_progress'
                                ? { ...s, status: 'error', durationMs: frozenStepDurationMs(s, 'error', s.startedAt) }
                                : s
                        ))
                        : prev
                ));
                // Sem isso, `msg.activity_step` (indicador inline dentro da bolha) fica
                // preso em 'in_progress' pra sempre nessa mensagem - o catch acima pode ter
                // empurrado uma bolha NOVA com o aviso de erro, então "última mensagem" não
                // é necessariamente a que está presa; varre todas em vez de só a última.
                // Congela em 'error' (ícone + "Falha, tente novamente", parado) em vez de
                // sumir - mesmo tratamento do painel, não esconde o que já tinha acontecido.
                const congelarActivityStepsPresos = prev => (
                    prev.some(m => m.activity_step?.status === 'in_progress')
                        ? prev.map(m => (
                            m.activity_step?.status === 'in_progress'
                                ? { ...m, activity_step: { ...m.activity_step, status: 'error' } }
                                : m
                        ))
                        : prev
                );
                setMessages(congelarActivityStepsPresos);
                setChatHistory(congelarActivityStepsPresos);
            }
        }
    };

    const handlePaste = (e) => {
        if (isLocalModel) return;
        if (isPreview && !assistantId) {
            e.preventDefault();
            return;
        }

        e.preventDefault();

        const items = e.clipboardData.items;

        for (let item of items) {
            if (item.type.startsWith("image/")) {
                const file = item.getAsFile();

                if (file) {
                    const newFile = new File([file], `pasted-image-${Date.now()}.png`, {
                        type: file.type,
                    });

                    handleFilesSelected([newFile]);
                }
            }
        }

        // No chat real o campo é contentEditable: força colar como texto puro
        // para não herdar formatação/HTML da área de transferência (no textarea
        // comum do chat de prévia isto é redundante, mas inofensivo).
        const text = e.clipboardData.getData('text/plain');
        if (text) {
            document.execCommand('insertText', false, text);
        }
    };

    const handleSend = async (event) => {
        if (event && typeof event.preventDefault === 'function') {
            event.preventDefault();
        }

        if (isUploading || cooldown) return;
        if (isPreview && !assistantId) return;
        await sendMessage();
    };

    // Tarefa 5.2 (FR-024): indice da ULTIMA ocorrencia de "/" em `value` que
    // conta como inicio de comando - no inicio da mensagem, ou imediatamente
    // precedida por espaco. Nunca no meio de uma palavra, para evitar falso
    // positivo em datas ("10/08/2026"), fracoes/conjuncoes ("e/ou"), unidades
    // ("km/h") ou caminhos de arquivo colados - mesmo criterio usado no
    // backend (skills_detection.py::_find_valid_slash_index). -1 se nenhuma.
    const findSlashCommandIndex = (value) => {
        for (let i = value.length - 1; i >= 0; i--) {
            if (value[i] !== '/') continue;
            if (i === 0 || /\s/.test(value[i - 1])) return i;
        }
        return -1;
    };

    // Mecânica do atalho "/": abre/fecha o dropdown de skills quando a última
    // "/" válida da mensagem (Tarefa 5.2 - não precisa mais ser o primeiro
    // caractere) ainda está sendo composta. A lista de skills exibida dentro
    // dele (Tarefa 2.1/5.9) é buscada de novo a cada abertura do dropdown, não
    // aqui - ver useEffect [showSlashMenu] abaixo.
    // Usado tanto no chat "de verdade" quanto no chat de prévia/testbox de um
    // Self Assistant (specs/self-assistant-skills). `[LIBERADO PARA MOBILE]`:
    // ate entao tambem nao abria em telas mobile (isMobile) - restricao
    // levantada por decisao do usuario, escopo restrito a listar/usar/criar
    // skills (o modal "Gerenciador de Skills"/tela "Detalhe da Skill"
    // continuam desktop-only).
    const handleMessageInput = (e) => {
        const value = e.currentTarget.textContent || '';
        setInput(value);
        // Uma vez que o usuario digita um espaco apos a "/" valida, a porcao
        // de nome do comando terminou (mesmo corte usado pelo backend em
        // skills_detection.py::detectar_skill, "/nome resto") - o resto e
        // mensagem livre, entao o dropdown de selecao deixa de fazer sentido.
        const slashIndex = findSlashCommandIndex(value);
        const isComposingSlashCommand = slashIndex !== -1 && !/\s/.test(value.slice(slashIndex + 1));
        setSlashCommandIndex(isComposingSlashCommand ? slashIndex : -1);
        setShowSlashMenu(isComposingSlashCommand);
    };

    const clearMessageInput = () => {
        setInput('');
        setShowSlashMenu(false);
        setSlashCommandIndex(-1);
        setPendingSkillCommandText(null);
        if (messageInputRef.current) {
            messageInputRef.current.textContent = '';
        }
    };

    const placeCaretAtEnd = (node) => {
        const range = document.createRange();
        range.selectNodeContents(node);
        range.collapse(false);
        const selection = window.getSelection();
        selection.removeAllRanges();
        selection.addRange(range);
    };

    // Cria o <span> azul de um comando de skill ("/skill-creator" ou
    // "/nome-da-skill") como um "token" atomico: contentEditable=false impede
    // o navegador de posicionar o cursor DENTRO do span, que e' a causa raiz
    // de texto digitado depois herdar o destaque azul (o "typing style" do
    // Chrome soh olha para ancestrais editaveis, nao mais para este span).
    const createSlashCommandSpan = (commandText) => {
        const commandSpan = document.createElement('span');
        commandSpan.className = 'slash-command-highlight';
        commandSpan.contentEditable = 'false';
        commandSpan.textContent = commandText;
        return commandSpan;
    };

    // FR-004: ao escolher "Criar Skill", insere o comando /skill-creator em azul
    // seguido do texto de ajuda em preto, pronto para o usuário completar/enviar.
    // Tarefa 5.2 (FR-024): quando a "/" que abriu o dropdown não era o
    // primeiro caractere da mensagem (slashCommandIndex >= 0), preserva o
    // texto digitado antes dela em vez de substituir a mensagem inteira -
    // não se aplica quando esta função é disparada de fora do dropdown
    // (botão "Criar com Iris"/"Criar Skill" fora do fluxo de "/", Tarefas
    // 1.1/3.4), caso em que slashCommandIndex fica em -1 e o comportamento
    // de substituir tudo permanece igual ao de antes.
    const handleSelectCreateSkill = () => {
        const commandText = `/${SKILL_CREATOR_NAME}`;
        const helperText = tChat('create_skill_helper_text');
        const preamble = slashCommandIndex >= 0 ? input.slice(0, slashCommandIndex) : '';
        const node = messageInputRef.current;

        if (node) {
            node.textContent = '';
            if (preamble) {
                node.appendChild(document.createTextNode(preamble));
            }
            node.appendChild(createSlashCommandSpan(commandText));
            node.appendChild(document.createTextNode(` ${helperText}`));
            node.focus();
            placeCaretAtEnd(node);
        }

        setInput(`${preamble}${commandText} ${helperText}`);
        setShowSlashMenu(false);
        setSlashCommandIndex(-1);
        setPendingSkillCommandText(commandText);
    };

    // Tarefa 3.4 (FR-014): "Criar com Iris", no modal "Gerenciador de Skills"
    // (renderizado dentro do componente irmao Historico), dispara este mesmo
    // fluxo por meio de um sinal incrementado pelo pai comum (Chat/index.js) -
    // os dois componentes nao tem referencia direta um ao outro. Ignora o
    // valor inicial (0) para nao disparar a insercao ao montar o chat.
    useEffect(() => {
        if (createSkillSignal > 0) {
            handleSelectCreateSkill();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [createSkillSignal]);

    // FR-005: ao escolher uma skill existente na lista (Tarefa 2.1), insere
    // "/nome-da-skill" em azul (mesmo padrao do "/skill-creator" da Tarefa 1.1),
    // pronto para o usuario completar/enviar o resto da mensagem em texto normal.
    // Tarefa 5.2 (FR-024): mesma logica de preservar o preambulo (texto antes
    // da "/" que abriu o dropdown) usada em handleSelectCreateSkill.
    const handleSelectSkill = (skill) => {
        const commandText = `/${skill.name}`;
        const preamble = slashCommandIndex >= 0 ? input.slice(0, slashCommandIndex) : '';
        const node = messageInputRef.current;

        if (node) {
            node.textContent = '';
            if (preamble) {
                node.appendChild(document.createTextNode(preamble));
            }
            node.appendChild(createSlashCommandSpan(commandText));
            // Espaco real (nao zero-width) logo apos o span: da ao cursor um
            // lugar para "aterrissar" fora do token atomico, igual ao texto de
            // ajuda do FR-004, e evita que o usuario cole o resto da mensagem
            // direto no nome do comando (ex.: "/minha-skillOi" em vez de
            // "/minha-skill Oi") - o backend separa nome/resto pelo primeiro
            // espaco (skills_detection.py::_extrair_comando).
            node.appendChild(document.createTextNode(' '));
            node.focus();
            placeCaretAtEnd(node);
        }

        setInput(`${preamble}${commandText} `);
        setShowSlashMenu(false);
        setSlashCommandIndex(-1);
        setPendingSkillCommandText(commandText);
    };

    // Tarefa 5.1 (FR-023): clicar num SkillCard do modal "Gerenciador de
    // Skills" (renderizado dentro do componente irmao Historico) dispara este
    // mesmo fluxo de insercao por meio de um sinal { skill, seq } atualizado
    // pelo pai comum (Chat/index.js) - mesmo mecanismo ja usado por
    // createSkillSignal (Tarefa 3.4), mas carregando qual skill foi
    // escolhida. Ignora o valor inicial (null) para nao disparar ao montar.
    useEffect(() => {
        if (selectSkillRequest) {
            handleSelectSkill(selectSkillRequest.skill);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectSkillRequest]);

    // Tarefa 6.4 (FR-038/FR-039): "Editar skill no chat" no menu de opcoes da
    // tela "Detalhe da Skill" - mesmo padrao visual de handleSelectCreateSkill
    // (comando azul + texto de ajuda em preto), mas com texto proprio ("Me
    // ajude a editar essa skill") e marcando pendingSkillEditIntent, que
    // sendMessage inclui como campo oculto na proxima requisicao ao backend
    // (sem aparecer no texto visivel da mensagem).
    const handleEditSkillInChat = (skill) => {
        const commandText = `/${skill.name}`;
        const helperText = tChat('edit_skill_helper_text');
        const preamble = slashCommandIndex >= 0 ? input.slice(0, slashCommandIndex) : '';
        const node = messageInputRef.current;

        if (node) {
            node.textContent = '';
            if (preamble) {
                node.appendChild(document.createTextNode(preamble));
            }
            node.appendChild(createSlashCommandSpan(commandText));
            node.appendChild(document.createTextNode(` ${helperText}`));
            node.focus();
            placeCaretAtEnd(node);
        }

        setInput(`${preamble}${commandText} ${helperText}`);
        setShowSlashMenu(false);
        setSlashCommandIndex(-1);
        setPendingSkillEditIntent(true);
        setPendingSkillCommandText(commandText);
    };

    // Tarefa 6.4: "Editar skill no chat" no menu de opcoes da tela "Detalhe da
    // Skill" (renderizada dentro do SkillsManagerModal, dentro do componente
    // irmao Historico) dispara este fluxo por meio do mesmo mecanismo de
    // sinal { skill, seq } ja usado por selectSkillRequest (Tarefa 5.1).
    // Ignora o valor inicial (null) para nao disparar ao montar.
    useEffect(() => {
        if (editSkillRequest) {
            handleEditSkillInChat(editSkillRequest.skill);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editSkillRequest]);

    // Skills do dropdown "/" filtradas pelo texto digitado depois da barra -
    // sem isso, o dropdown sempre mostrava a lista completa (Tarefa 2.1),
    // ignorando o que o usuario digitou apos "/".
    const slashQuery = slashCommandIndex >= 0 ? input.slice(slashCommandIndex + 1) : '';
    // A skill-creator ja vive no banco como skill real, publica (visivel em
    // availableSkills como qualquer outra), mas e' tratada como skill de
    // plataforma: exclusiva do botao fixo "+ Criar Skill", nunca aparece
    // como linha nesta lista (nem o item sintetico que existia aqui antes,
    // ja removido, nem a skill real vinda da API).
    const filteredSkills = availableSkills
        .filter((skill) => skill.name !== SKILL_CREATOR_NAME)
        .filter((skill) => matchesSlashQuery(skill, slashQuery));

    const handleMessageKeyDown = (e) => {
        if (showSlashMenu) {
            // Total de itens navegaveis: "Criar Skill" (index 0) + skills
            // carregadas (enquanto skillsLoading, so "Criar Skill" existe).
            // `[REVISADO na Tarefa 11.5, FR-078]`: com skillsError, o bloco de
            // erro substitui a lista visualmente, mas `availableSkills` pode
            // ainda ter itens de uma busca anterior bem-sucedida (nao e'
            // limpo no fetch que falhou) - sem esse `|| skillsError` aqui, o
            // teclado navegaria/selecionaria itens "fantasma" que nao estao
            // mais visiveis.
            const totalItems = 1 + (skillsLoading || skillsError ? 0 : filteredSkills.length);

            if (e.key === 'Escape') {
                e.preventDefault();
                setShowSlashMenu(false);
                setSlashCommandIndex(-1);
                return;
            }
            if (e.key === 'ArrowDown') {
                e.preventDefault();
                setHighlightedIndex((i) => Math.min(i + 1, totalItems - 1));
                return;
            }
            if (e.key === 'ArrowUp') {
                e.preventDefault();
                setHighlightedIndex((i) => Math.max(i - 1, 0));
                return;
            }
            // Tab tem a mesma funcao do Enter aqui: escolhe o item destacado
            // do dropdown "/" (padrao comum de autocomplete) - sem o
            // preventDefault, Tab tiraria o foco do campo em vez de selecionar.
            if ((e.key === 'Enter' && !e.shiftKey) || e.key === 'Tab') {
                e.preventDefault();
                if (highlightedIndex === 0) {
                    handleSelectCreateSkill();
                } else {
                    handleSelectSkill(filteredSkills[highlightedIndex - 1]);
                }
                return;
            }
        }

        if ((isUploading || cooldown) && e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            return;
        }
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    const cleanChat = () => {
        if (!isPreview) return;
        try {
            const generatedSessionId = newSessionId ? newSessionId() : null;
            if (!generatedSessionId) return;

            setSummaryInfo({ label: tChat('newChatLabel'), status: false, session_id: generatedSessionId });

            const history = [{ role: 'system', content: '' }];
            if (setSessionIdFromContext) {
                setSessionIdFromContext(generatedSessionId);
            }
            setMessages(history);
            setChatHistory(history);
            setSelectedFiles([]);
            setUploadedFiles([]);
            setInteractionId(null);
        } catch (error) {
            console.error('Erro ao criar nova sess�o:', error);
        }
    };

    const handleSuggestionClick = (promptKey) => {
        // Use the prompt translation key (expected to be in imperative form)
        const promptText = tChat(promptKey);

        sendMessage({
            text: promptText,
            files: [],
            resetSelectedFiles: false,
            resetFileInput: false
        });
    };

    // Detecta se o usu�rio est� no final do scroll
    const isUserAtBottom = (element) => {
        const threshold = 50; // px de toler�ncia
        return element.scrollHeight - element.scrollTop - element.clientHeight < threshold;
    };

    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        const handleScroll = () => {
            if (isUserAtBottom(container)) {
                setAutoScroll(true);
            } else {
                setAutoScroll(false);
            }
        };

        container.addEventListener("scroll", handleScroll);
        return () => container.removeEventListener("scroll", handleScroll);
    }, []);

    // useEffect para rolar at� o final sempre que as mensagens mudarem
    useEffect(() => {
        if (!autoScroll) return;

        const timeout = setTimeout(() => {
            requestAnimationFrame(() => {
                messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
            });
        }, 0);

        return () => clearTimeout(timeout);
    }, [messages, autoScroll]);

    useEffect(() => {
        clearMessageInput();
    }, [loading, sessionId]);

    useEffect(() => {
        function handleClickOutside(event) {
            if (menuRef.current && !menuRef.current.contains(event.target)) {
                setShowAttachMenu(false);
            }
        }
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        function handleClickOutsideSlashMenu(event) {
            if (slashMenuRef.current && !slashMenuRef.current.contains(event.target)) {
                setShowSlashMenu(false);
                setSlashCommandIndex(-1);
            }
        }
        document.addEventListener('mousedown', handleClickOutsideSlashMenu);
        return () => document.removeEventListener('mousedown', handleClickOutsideSlashMenu);
    }, []);

    // Tarefa 2.2: destaque de teclado comeca sempre em "Criar Skill" (index 0)
    // a cada vez que o dropdown "/" e reaberto, e tambem a cada letra digitada
    // apos a "/" - sem isso, filtrar a lista podia deixar highlightedIndex
    // apontando para um item que nao existe mais na lista filtrada.
    useEffect(() => {
        if (showSlashMenu) {
            setHighlightedIndex(0);
        }
    }, [showSlashMenu, slashQuery]);

    // Tarefa 2.2: ao navegar pela lista de skills via teclado, garante que o
    // item destacado fique visivel dentro da area rolavel (.slash-menu-skill-list),
    // mesmo quando esta fora da porcao visivel sem rolagem (FR-021).
    useEffect(() => {
        if (!showSlashMenu || highlightedIndex === 0) return;
        const activeItem = slashMenuRef.current?.querySelector('.slash-menu-item--active');
        activeItem?.scrollIntoView({ block: 'nearest' });
    }, [highlightedIndex, showSlashMenu]);

    // Tarefa 5.9 (FR-003, User Story 2): busca as skills disponiveis para o
    // usuario (publicas TPF + proprias + compartilhadas, aba consolidada
    // "todas" de GET /get_skills) a cada vez que o dropdown "/" e aberto
    // (showSlashMenu passa a true) - nao mais uma unica vez no mount do chat
    // (Tarefa 2.1). Refeito a cada abertura para refletir skills criadas
    // durante a própria conversa (ex.: via "/skill-creator"), que antes só
    // apareciam na lista depois de recarregar a página. Nao se aplica ao chat
    // de previa/testbox (isPreview retorna antes de montar este trecho da UI -
    // ver `if (isPreview)` acima). `[LIBERADO PARA MOBILE]`: ate entao tambem
    // nao rodava em telas mobile (isMobile) - restricao levantada por decisao
    // do usuario (autorizar listar/usar/criar skills via "/" tambem no chat
    // mobile); o modal "Gerenciador de Skills" e a tela "Detalhe da Skill"
    // continuam desktop-only, sem mudanca. `[REVISADO na Tarefa 11.5, FR-078]`: falha/timeout deixa de
    // ser tratada como lista vazia - marca `skillsError` (distinto de "sem
    // skills") e mantem `availableSkills` como estava antes da tentativa
    // falha. `loadAvailableSkills` e reutilizavel pelo efeito abaixo (abrir o
    // dropdown) e pelo botao "tentar de novo" (chamada manual).
    //
    // Gap encontrado ao validar User Story 2/3 (specs/self-assistant-skills/
    // plan.md): dentro de um Self Assistant, o dropdown "/" precisa mostrar
    // as skills VINCULADAS a ESTE assistant (GET /get_assistant_skills_for_chat,
    // acessivel a qualquer usuario com acesso ao assistant, nao so' admin -
    // diferente de GET /get_assistant_skills, que e' da tela de configuracao)
    // - nao a lista geral de skills do usuario, que e' o que "0"/"1"/"2"/"3"
    // (chat Iris e os assistentes fixos) continuam usando.
    const loadAvailableSkills = useCallback(() => {
        const fetchId = ++skillsFetchIdRef.current;
        const isSelfAssistant = assistantId && !["0", "1", "2", "3"].includes(assistantId);
        setSkillsLoading(true);
        setSkillsError(false);

        const url = isSelfAssistant
            ? `${process.env.REACT_APP_API_URL}/get_assistant_skills_for_chat?email=${encodeURIComponent(resolvedUser)}&assistant_id=${encodeURIComponent(assistantId)}`
            : `${process.env.REACT_APP_API_URL}/get_skills?email=${encodeURIComponent(resolvedUser)}&tab=todas`;

        fetch(url, {
            headers: { "Authorization": `Bearer ${accessToken}` },
        })
            .then((response) => {
                if (!response.ok) throw new Error(`GET /get_skills falhou (${response.status})`);
                return response.json();
            })
            .then((data) => {
                if (fetchId !== skillsFetchIdRef.current) return;
                setAvailableSkills(data.skills || []);
            })
            .catch(() => {
                if (fetchId !== skillsFetchIdRef.current) return;
                setSkillsError(true);
            })
            .finally(() => {
                if (fetchId !== skillsFetchIdRef.current) return;
                setSkillsLoading(false);
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [resolvedUser, accessToken, assistantId]);

    useEffect(() => {
        if (!showSlashMenu) return;
        loadAvailableSkills();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showSlashMenu]);

    if (isPreview) {
        const rootClassName = `${classeCSS} ${assistant === 'self-assistant' ? 'test-box--self' : ''}`.trim();

        return (
            <div className={rootClassName}>

                <div className="restart-chat-container">
                    <div className="restart-chat-btn">
                        <img
                            src="/imagens/newArrowsClockwise.svg"
                            alt=""
                            className="restart-chat"
                            onClick={cleanChat}
                            onMouseEnter={() => setShowTooltip(true)}
                            onMouseLeave={() => setShowTooltip(false)}
                        />
                    </div>

                    {showTooltip && <div className="tooltip-restart">{tAssistantConfiguration("restart_chat")}</div>}

                    <div className="assistant-preview-title">
                        {!['1', '2', '3'].includes(assistantId) && (
                            <div>{tAssistantConfiguration('assistant_preview')}</div>
                        )}
                    </div>

                    {level !== 'externo' && (
                        <a
                            href={infoUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            <img
                                src="/imagens/alert-circle.svg"
                                alt="Informações"
                                style={{ cursor: "pointer", width: 28, height: 25 }}
                                title={tChat('prompt_guide')}
                            />
                        </a>
                    )}

                </div>

                <section className='messages-container'>
                    {messages.map((msg, index) => (
                        msg.content || msg.activity_step || (isSending && index === messages.length - 1) ? (
                            <Message
                                key={msg.clientId || msg._id || index}
                                msg={msg}
                                index={index}
                                sessionId={sessionId}
                                isSending={isSending && index === messages.length - 1}
                                assistant={assistant}
                                // Na prévia não existe ActivityPanel pra abrir (sem espaço na
                                // tela) - isActivityHistoryOpen sempre false aqui, senão o pill
                                // (FR-013) ficaria escondido pra sempre depois do 1º clique, já
                                // que nada nesse contexto volta activityPanelOpen pra false.
                                isActivityHistoryOpen={false}
                                hideActivitySummaryCaret
                            />
                        ) : null
                    ))}
                    <div ref={messagesEndRef} />
                </section>

                <section className="input-container-self-assistant">
                    <div
                        style={{
                            padding: '29.5px 20px',
                            backgroundColor: isInputFocused ? '#E8F0F8' : 'transparent',
                            borderRadius: '24px',
                            width: '80%',
                            border: isInputFocused ? 'none' : '1px solid #D4EAFF',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'start',
                            gap: '10px',
                            boxSizing: 'border-box',
                            boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                            opacity: assistantId ? 1 : 0.6,
                            cursor: assistantId ? 'auto' : 'not-allowed'
                        }}
                        aria-disabled={!assistantId}
                    >
                        {/* `overflow: 'auto'` removido - mesma causa do bug do
                            dropdown "/" ja corrigida na div interna logo abaixo:
                            esta caixa nao tem maxHeight, entao o overflow nunca
                            fazia diferenca visual, so cortava o dropdown (que
                            abre para cima, fora dos limites desta caixa). */}
                        {/* Chips dos arquivos dentro da caixa */}
                        {selectedFiles.length > 0 && (
                            <div
                                className="file-preview-container"
                                style={{ backgroundColor: isInputFocused ? '#E8F0F8' : 'transparent', }}
                            >
                                {selectedFiles.map((file, index) => (

                                    <UploadButton
                                        key={index}
                                        filename={file.name || file.filename}
                                        onDelete={() => {
                                            setSelectedFiles(prev => prev.filter((_, i) => i !== index));
                                        }}
                                        index={index}
                                        width="100%"
                                        isUploading={isUploading}
                                    />

                                ))}
                            </div>
                        )}
                        <div style={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'flex-end',
                            gap: '8px',
                            width: '100%',
                            height: 'auto',
                            position: 'relative'
                        }}>
                            {/* Upload */}
                            <label htmlFor="file-upload" style={{ cursor: (isUploading || !assistantId) ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center' }}>
                                <img src="imagens/upload4.svg" alt="upload" style={{ width: 24, height: 24 }} />
                                <input
                                    id="file-upload"
                                    ref={fileInputRef}
                                    type="file"
                                    multiple
                                    disabled={!assistantId}
                                    style={{ display: 'none' }}
                                    onChange={(e) => {
                                        const files = Array.from(e.target.files || []);
                                        handleFilesSelected(files);
                                        if (fileInputRef.current) fileInputRef.current.value = null;
                                    }}
                                />
                            </label>
                            {/* `overflowY: 'auto'` removido deste wrapper - Ponto 1
                                (specs/updated_self_assistant): clipava o dropdown "/"
                                (position: absolute, bottom: 100%, ou seja acima da caixa),
                                deixando-o invisivel no chat de previa. O textarea logo
                                abaixo (.custom-textarea) ja tem seu proprio overflowY:
                                'auto' para o crescimento do texto, entao nao faz falta
                                aqui. */}
                            {/* Skills (atalho "/") habilitado tambem no chat de previa (testbox)
                                dentro de um Self Assistant, especs/self-assistant-skills - mesmo
                                campo contentEditable com destaque azul do comando ja usado no
                                chat real (handleMessageInput/handleMessageKeyDown), em vez do
                                textarea simples de antes, pra manter paridade de comportamento. */}
                            {/* Campo de digitação */}
                            {input === '' && (
                                <span
                                    className="message-input-placeholder"
                                    style={{ position: 'absolute', left: "32px", top: '2px', fontSize: 14, color: '#8a8a8a' }}
                                >
                                    {tChat('write_message')}
                                </span>
                            )}
                            <div
                                ref={messageInputRef}
                                contentEditable={Boolean(assistantId)}
                                onInput={handleMessageInput}
                                className="custom-textarea"
                                style={{
                                    overflowY: 'auto',
                                    fontSize: 14,
                                    flex: 1,
                                    border: 'none',
                                    outline: 'none',
                                    fontFamily: 'inherit',
                                    backgroundColor: 'transparent',
                                    color: '#000',
                                    padding: 0,
                                    minHeight: '20px',
                                    maxHeight: '84px',
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-word',
                                    opacity: assistantId ? 1 : 0.6,
                                    cursor: assistantId ? 'text' : 'not-allowed',
                                }}
                                onFocus={() => setIsInputFocused(true)}
                                onBlur={() => setIsInputFocused(false)}
                                onKeyDown={handleMessageKeyDown}
                                data-testid="input-message"
                                onPaste={handlePaste}
                            />

                            {showSlashMenu && (
                                <div
                                    ref={slashMenuRef}
                                    data-testid="slash-menu"
                                    className="slash-menu"
                                    style={{
                                        position: 'absolute',
                                        bottom: '100%',
                                        left: '0',
                                        marginBottom: '8px',
                                        backgroundColor: '#fff',
                                        borderRadius: '12px',
                                        boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                                        zIndex: 1000,
                                        minWidth: '220px',
                                        maxWidth: '320px',
                                        padding: '8px',
                                        border: '1px solid #E0E0E0'
                                    }}
                                >
                                    <button
                                        type="button"
                                        className={`slash-menu-item${highlightedIndex === 0 ? ' slash-menu-item--active' : ''}`}
                                        data-testid="slash-menu-create-skill"
                                        onMouseDown={(e) => e.preventDefault()}
                                        onMouseEnter={() => setHighlightedIndex(0)}
                                        onClick={handleSelectCreateSkill}
                                    >
                                        <span className="slash-menu-item-icon" aria-hidden="true">+</span>
                                        <span className="slash-menu-item-label">{tChat('create_skill')}</span>
                                    </button>

                                    {!skillsLoading && skillsError && (
                                        <div className="slash-menu-item slash-menu-error" data-testid="slash-menu-error">
                                            <span className="slash-menu-error-message">
                                                {tChat('skills_manager_load_error_message')}
                                            </span>
                                            <button
                                                type="button"
                                                className="slash-menu-error-retry"
                                                data-testid="slash-menu-retry"
                                                onMouseDown={(e) => e.preventDefault()}
                                                onClick={loadAvailableSkills}
                                            >
                                                {tChat('skills_manager_load_error_retry')}
                                            </button>
                                        </div>
                                    )}

                                    {!skillsError && (skillsLoading || filteredSkills.length > 0) && (
                                        <div className="slash-menu-skill-list" data-testid="slash-menu-skill-list">
                                            {skillsLoading && (
                                                <div className="slash-menu-item slash-menu-loading" data-testid="slash-menu-loading">
                                                    <CircularProgress size={16} thickness={5} />
                                                </div>
                                            )}

                                            {!skillsLoading && filteredSkills.map((skill, index) => (
                                                <button
                                                    type="button"
                                                    key={skill.skill_id}
                                                    className={`slash-menu-item${highlightedIndex === index + 1 ? ' slash-menu-item--active' : ''}`}
                                                    data-testid="slash-menu-skill-item"
                                                    onMouseDown={(e) => e.preventDefault()}
                                                    onMouseEnter={() => setHighlightedIndex(index + 1)}
                                                    onClick={() => handleSelectSkill(skill)}
                                                    title={skill.description}
                                                >
                                                    <img
                                                        src="/imagens/skills-icon.svg"
                                                        alt=""
                                                        className="slash-menu-item-icon"
                                                        aria-hidden="true"
                                                        width={18}
                                                        height={15}
                                                    />
                                                    <span className="slash-menu-item-label" >{skill.name}</span>
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Linha com upload/enviar ao lado do textarea */}

                            {/* Linha vertical */}
                            <div style={{ width: '1.5px', height: '24px', backgroundColor: '#C1C1C1' }} />

                            {/* Enviar */}
                            <img
                                data-testid="enter-icon"
                                src="imagens/ArrowCircleRight2.svg"
                                alt="enviar"
                                onClick={handleSend}
                                style={{
                                    width: 24,
                                    height: 24,
                                    cursor: (isUploading || cooldown || !assistantId) ? 'not-allowed' : 'pointer',
                                }}
                            />
                        </div>
                    </div>
                </section>

                {showAlert && (
                    <AlertsCustom
                        type={alertType}
                        title={alertTitle}
                        message={alertMessage}
                        onClose={() => setShowAlert(false)}
                    />
                )}

            </div>
        );
    }

    // Se o painel de sugest�es estiver vis�vel (assistente == '0' e ainda n�o h� mensagens de usu�rio),
    // ent�o esconderemos as mensagens do assistente e aplicaremos flex-grow ao container de mensagens.
    const showSuggestion = assistantId === '0' && messages.filter(msg => msg.role === 'user').length === 0;

    // Oculta mensagens iniciais (message-wrapper) para self assistant, Sesan e References quando ainda n�o houve intera��o do usu�rio.
    const isSelfAssistant = assistantId && !['0', '1', '2', '3'].includes(assistantId);
    const hideInitialMessages = isChatEmpty && (assistantId === '1' || assistantId === '2' || isSelfAssistant);

    // Mensagens que ser�o efetivamente renderizadas
    const renderedMessages = hideInitialMessages
        ? []
        : (showSuggestion ? messages.filter(msg => msg.role !== 'assistant') : messages);

    return (
        <main
            className={`${classeCSS} ${isMobile && keyboardOpen && showSuggestion ? 'keyboard-open' : ''}`}
            data-testid="chat-shell"
        >

            <header className='chatbox-superior' data-testid='chatbox-superior'>
                <div className={`chatbox-buscaweb ${isMobile ? 'mobile' : 'desktop'}`} data-testid='chatbox-buscaweb'>
                    <div className='chatbox-superior-left'>
                        {isMobile && (
                            <img
                                src="/imagens/three-bars.svg"
                                alt={tChat('menuIcon')}
                                style={{ cursor: "pointer", width: 23 }}
                                onClick={openHistoricoOverlay}
                            />
                        )}

                        <div className={`container-modelos ${isMobile ? 'mobile' : 'desktop'}`}>
                            <Modelos
                                setModel={handleSetModel}
                                defaultModel={effectiveModel}
                                sessionId
                                extraModels={localClusterModel ? [{ value: localClusterModel, label: `${localClusterModelVision} ${localClusterModel}` }] : []}
                            />
                        </div>

                        {isMobile && <img src="/imagens/new_chat_icon.svg" alt={tChat('newChatIcon')} style={{ cursor: "pointer", width: 24 }} onClick={createNewChat} />}

                    </div>

                </div>

                <div>{(assistantId === '1' || assistantId === '182') && !isMobile && <Filtros filtro={filtro} setFiltro={setFiltro} chatHistory={chatHistory} assistantId={assistantId} sessionId={sessionId} />}</div>

                {!isMobile && level !== 'externo' &&
                    <a
                        href={infoUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        <img
                            src="/imagens/alert-circle.svg"
                            alt={tChat('infoIcon')}
                            style={{ cursor: "pointer", width: 28, height: 25 }}
                            title={tChat('prompt_guide')}
                        />
                    </a>
                }


            </header>

            {!(isMobile && keyboardOpen && showSuggestion) && (
                <section
                    ref={containerRef}
                    className={`messages-container ${isMobile && !keyboardOpen ? '' : ''} ${!isChatEmpty ? 'expand' : ''}`}
                    style={showSuggestion ? { flexGrow: 1 } : (assistantId !== '0' && isChatEmpty) ? { flexGrow: 1 } : undefined}
                >
                    {loading ? (
                        <div className="loading-container-chat">
                            <CircularProgress /> {/* �cone de loading */}
                        </div>
                    ) : (
                        renderedMessages.map((msg, index) => (
                            <Message
                                key={idDaMensagem(msg, index)}
                                msg={msg}
                                index={index}
                                sessionId={sessionId}
                                // Aplica isSending apenas � �ltima mensagem do conjunto renderizado
                                isSending={isSending && index === renderedMessages.length - 1}
                                assistantId={assistantId}
                                links={msg.links || responseLinks || msg.references}
                                isActivityHistoryOpen={activityPanelOpen && activityPanelMessageId === idDaMensagem(msg, index)}
                                onActivityClick={hideActivityPanel ? undefined : () => { setActivityPanelMessageId(idDaMensagem(msg, index)); setActivityPanelOpen(true); }}
                                hideActivitySummaryCaret={hideActivityPanel}
                            />
                        ))
                    )}

                    <div ref={messagesEndRef} />
                </section>
            )}

            {/* Wrapper para centralizar título e input quando teclado está aberto no mobile */}
            <div className={`mobile-center-wrapper ${isMobile && keyboardOpen && showSuggestion ? 'active' : ''}`}>
                {messages.filter(msg => msg.role === 'user').length === 0 && (
                    <span className='title_suggestion'>{tChat('how_can_i_help')}</span>
                )}

                {assistantId === '0' && !loading && messages.filter(msg => msg.role === 'user').length === 0 && (
                    <section className='suggestion_panel_wrapper' data-testid='suggestion_panel_wrapper'>
                        {(!isMobile || !keyboardOpen) && (
                            <div className='cards_suggestion'>
                                {suggestionButtons
                                    .map(({ labelKey, promptKey, className, image }) => (
                                        <button
                                            key={promptKey}
                                            type='button'
                                            className={className}
                                            data-testid={className}
                                            onClick={() => handleSuggestionClick(promptKey)}
                                        >
                                            <div className="card-text-content">
                                                <div className="card-subtitle">
                                                    {tChat(labelKey)}
                                                </div>
                                            </div>
                                        </button>
                                    ))}
                            </div>
                        )}
                    </section>
                )}

                <section className={`input-container ${keyboardOpen && assistantId !== '0' && isMobile ? 'keyboard-on' : ''}`} style={isChatEmpty && !(isMobile && keyboardOpen) ? { flexGrow: 1 } : {}}>
                    <div className="input-warning" >
                        <p>
                            {isMobile
                                ? tChat('msg_small')
                                : tChat('msg_aviso')
                            }
                        </p>

                    </div>
                    <div
                        style={{
                            padding: '8px 4px',
                            backgroundColor: isInputFocused ? '#E8F0F8' : 'transparent',
                            borderRadius: '23px',
                            width: '100%',
                            maxWidth: '800px',
                            border: '1px solid #D4EAFF',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '10px',
                            boxSizing: 'border-box',
                            position: 'relative',
                            alignItems: 'flex-start',
                            margin: '0 auto',
                            boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)"
                        }}
                    >
                        {/* Chips dos arquivos dentro da caixa */}
                        {selectedFiles.length > 0 && (
                            <div
                                className="file-preview-container"
                                style={{ backgroundColor: isInputFocused ? '#E8F0F8' : 'transparent', }}
                            >
                                {selectedFiles.map((file, index) => (

                                    <UploadButton
                                        key={index}
                                        filename={file.filename || file.name}
                                        onDelete={() => {
                                            setSelectedFiles(prev => prev.filter((_, i) => i !== index));
                                        }}
                                        index={index}
                                        width="100%"
                                        isUploading={isUploading}
                                    />

                                ))}
                            </div>
                        )}

                        {/* Container Principal Externo - Garante que nada saia da tela */}
                        <section style={{ padding: '10px', width: '100%' }}>

                            {/* Menu Suspenso Isolado (Renderiza acima do input) */}
                            {showAttachMenu && (

                                <div
                                    ref={menuRef}
                                    style={{
                                        position: 'absolute',
                                        bottom: '100%',
                                        left: '0',
                                        marginBottom: '8px',
                                        backgroundColor: '#fff',
                                        borderRadius: '12px',
                                        boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                                        zIndex: 1000,
                                        minWidth: '150px',
                                        overflow: 'hidden',
                                        padding: '10px',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: '4px',
                                        border: '1px solid #E0E0E0',
                                        width: '130px'
                                    }}>
                                    {/* Opções do Menu */}
                                    {level !== 'externo' && (
                                        <div
                                            style={{ marginBottom: '8px', alignItems: 'center', display: 'flex', gap: '6px', borderRadius: '8px' }}
                                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f5f5f5'}
                                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                        // Ao clicar na linha, ele tenta clicar no botão interno

                                        >
                                            <OneDriveFileButton
                                                showText={true}
                                                onFilesSelected={(files) => {
                                                    const normalizedFiles = normalizeOneDriveFiles(files);
                                                    setSelectedFiles(prev => [...prev, ...normalizedFiles]);
                                                    handleFileUpload(files, true);
                                                    setShowAttachMenu(false);
                                                }}
                                                isUploading={isUploading}
                                                filters={isLocalModel ? ['.docx', '.xlsx'] : undefined}
                                            />
                                        </div>
                                    )}

                                    <div style={{ marginBottom: '8px', alignItems: 'start', display: 'flex', gap: '6px', marginRight: '7px' }} className='one-drive-file-button'>
                                        <label
                                            htmlFor="file-upload"
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '12px',
                                                width: '100%',
                                                padding: '8px',
                                                borderRadius: '8px',
                                                cursor: isUploading ? 'not-allowed' : 'pointer',
                                                width: '100%',

                                            }}
                                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f5f5f5'}
                                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                        >
                                            <img
                                                src="imagens/upload4.svg"
                                                alt={tChat('uploadIcon')}
                                                style={{ width: 20, height: 20, marginRight: '5px' }}
                                            />

                                            <span>{tCommon('arquivos')}</span>

                                            <input
                                                data-testid="local-file-input"
                                                id="file-upload"
                                                ref={fileInputRef}
                                                type="file"
                                                multiple
                                                style={{ display: 'none' }}
                                                onChange={(e) => {
                                                    const files = Array.from(e.target.files);
                                                    handleFilesSelected(files);
                                                    setShowAttachMenu(false);
                                                }}
                                            />
                                        </label>

                                    </div>

                                </div>
                            )}

                            {/* Linha de Interação */}
                            <div style={{
                                display: 'flex',
                                alignItems: 'flex-end',
                                gap: '8px',
                                position: 'relative' // Referência para o menu suspenso
                            }}>

                                {showSlashMenu && (
                                    <div
                                        ref={slashMenuRef}
                                        data-testid="slash-menu"
                                        className="slash-menu"
                                        style={{
                                            position: 'absolute',
                                            bottom: '100%',
                                            left: '0',
                                            marginBottom: '24px',
                                            backgroundColor: '#fff',
                                            borderRadius: '12px',
                                            boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                                            zIndex: 1000,
                                            minWidth: '220px',
                                            maxWidth: '320px',
                                            padding: '8px',
                                            border: '1px solid #E0E0E0'
                                        }}
                                    >
                                        <button
                                            type="button"
                                            className={`slash-menu-item${highlightedIndex === 0 ? ' slash-menu-item--active' : ''}`}
                                            data-testid="slash-menu-create-skill"
                                            onMouseDown={(e) => e.preventDefault()}
                                            onMouseEnter={() => setHighlightedIndex(0)}
                                            onClick={handleSelectCreateSkill}
                                        >
                                            <span className="slash-menu-item-icon" aria-hidden="true">+</span>
                                            <span className="slash-menu-item-label">{tChat('create_skill')}</span>
                                        </button>

                                        {/* FR-078 (Tarefa 11.5): erro/timeout de GET /get_skills, distinto de
                                            "sem skills" - com acao para tentar de novo (mesmo estado de
                                            carregamento reaproveitado, loadAvailableSkills). */}
                                        {!skillsLoading && skillsError && (
                                            <div className="slash-menu-item slash-menu-error" data-testid="slash-menu-error">
                                                <span className="slash-menu-error-message">
                                                    {tChat('skills_manager_load_error_message')}
                                                </span>
                                                <button
                                                    type="button"
                                                    className="slash-menu-error-retry"
                                                    data-testid="slash-menu-retry"
                                                    onMouseDown={(e) => e.preventDefault()}
                                                    onClick={loadAvailableSkills}
                                                >
                                                    {tChat('skills_manager_load_error_retry')}
                                                </button>
                                            </div>
                                        )}

                                        {/* Lista de skills (Tarefa 2.1): rolável a partir do 6º item — "Criar
                                            Skill" fica fixo acima, fora da rolagem. Altura calculada para 5 itens
                                            visíveis (~38.6px cada, gap 8px), conforme node do Figma. */}
                                        {!skillsError && (skillsLoading || filteredSkills.length > 0) && (
                                            <div className="slash-menu-skill-list" data-testid="slash-menu-skill-list">
                                                {skillsLoading && (
                                                    <div className="slash-menu-item slash-menu-loading" data-testid="slash-menu-loading">
                                                        <CircularProgress size={16} thickness={5} />
                                                    </div>
                                                )}

                                                {!skillsLoading && filteredSkills.map((skill, index) => (
                                                    <button
                                                        type="button"
                                                        key={skill.skill_id}
                                                        className={`slash-menu-item${highlightedIndex === index + 1 ? ' slash-menu-item--active' : ''}`}
                                                        data-testid="slash-menu-skill-item"
                                                        onMouseDown={(e) => e.preventDefault()}
                                                        onMouseEnter={() => setHighlightedIndex(index + 1)}
                                                        onClick={() => handleSelectSkill(skill)}
                                                        title={skill.description}
                                                    >
                                                        <img
                                                            src="/imagens/skills-icon.svg"
                                                            alt=""
                                                            className="slash-menu-item-icon"
                                                            aria-hidden="true"
                                                            width={18}
                                                            height={15}
                                                        />
                                                        <span className="slash-menu-item-label" >{skill.name}</span>
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* 1. Botão de Mais (Anexos) */}
                                {!['1', '2', '3', '1118'].includes(assistantId) && (
                                    <button
                                        data-testid="attach-button"
                                        onClick={() => setShowAttachMenu(!showAttachMenu)}
                                        style={{
                                            background: 'none',
                                            border: 'none',
                                            padding: '8px 4px',
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            marginBottom: '0px' // Alinha com a base da linha simples
                                        }}
                                    >
                                        <span style={{ fontSize: '28px', color: '#0078D4', lineHeight: 1 }}>+</span>
                                    </button>
                                )}

                                {/* 2. Área de Digitação (A "Bolha") */}
                                <div style={{
                                    flex: 1,
                                    borderRadius: '20px',
                                    padding: '8px 16px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    minHeight: '40px',
                                    position: 'relative'
                                }}>
                                    {input === '' && (
                                        <span
                                            className="message-input-placeholder"
                                            style={{ left: 16, top: '12px', fontSize: '15px' }}
                                        >
                                            {tChat('write_message')}
                                        </span>
                                    )}
                                    <div
                                        ref={messageInputRef}
                                        contentEditable={Boolean(assistantId)}
                                        onInput={handleMessageInput}
                                        style={{
                                            width: '100%',
                                            border: 'none',
                                            outline: 'none',
                                            backgroundColor: 'transparent',
                                            fontSize: '15px',
                                            padding: '4px 0',
                                            minHeight: '20px',
                                            maxHeight: '84px',
                                            overflowY: 'auto',
                                            whiteSpace: 'pre-wrap',
                                            wordBreak: 'break-word',
                                            opacity: assistantId ? 1 : 0.6,
                                            cursor: assistantId ? 'text' : 'not-allowed',
                                        }}
                                        onFocus={() => setIsInputFocused(true)}
                                        onBlur={() => setIsInputFocused(false)}
                                        onKeyDown={handleMessageKeyDown}
                                        data-testid="input-message"
                                        onPaste={handlePaste}
                                    />
                                </div>

                                {/* 3. Botão de Enviar (Fora da bolha para nunca sumir) */}
                                <button
                                    type="button"
                                    data-testid="enter-icon"
                                    onClick={handleSend}
                                    disabled={isUploading || cooldown || !input.trim()}
                                    style={{
                                        background: 'none',
                                        border: 'none',
                                        cursor: (isUploading || cooldown) ? 'not-allowed' : 'pointer',
                                        padding: '8px 4px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        marginBottom: '0px',
                                        opacity: (isUploading || cooldown) ? 0.5 : 1
                                    }}
                                >
                                    <img
                                        src="imagens/ArrowCircleRight2.svg"
                                        style={{ width: '28px', height: '28px' }}
                                        alt="Enviar"
                                    />
                                </button>
                            </div>
                        </section>

                    </div>
                </section>

                {
                    showAlert && (
                        <AlertsCustom
                            type={alertType}
                            title={alertTitle}
                            message={alertMessage}
                            onClose={() => setShowAlert(false)}
                        />
                    )
                }
            </div >

            {/* {isMobile &&
                <div className='custo-sessao' style={{ marginTop: 10 }}>
                    <img src="/imagens/cifrao.svg" alt="" className="menu-item-icon" />
                    <p className="custo-texto"> {tChat("session_cost")} <span>U${cost}</span></p>
                </div>} */}

        </main >
    );

}

export default UnifiedChat;
